import {
    EzException,
    EzBadParamException,
    EzBadStateException
} from '/ezlibrary/exceptions/EzExceptions.js';
/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Import /ezlibrary/helpers/* individualy. Do not import from /ezlibrary/helpers/EzHelpers.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~e~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzJson } from '/ezlibrary/helpers/EzJson.js';
import { EzDomRect } from '/ezlibrary/ux/EzDomRect.js';

/**
 * @class
 * @description
 * EzClocker UX Helper Class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzUx } from '/ezlibrary/ux/EzUx.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Do not create new instances of this class!
 * Access is via static methods and properties only
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzUx {
    /**
     * @private @function
     * Logs the provided msg at the provided level.
     * @param {string} msg
     * @param {string} level
     * Level can be one of the followign: 'info', 'warn', 'error', or 'debug' - if it is neither then it defaults to 'debug'
     * @param {undefined|null|Error|object} err
     * Optional exception (err) object to pass to the logger.
     */
    static #log(msg = EzString.EMPTY, level = 'debug', err = null) {
        if (!EzString.hasText(msg)) {
            return;
        }

        if (!EzString.hasText(level) || ('info' !== level && 'warn' !== level && 'error' !== level && 'debug' !== level)) {
            level = 'debug';
        }

        if (EzFunction.isFunction(globalThis?.ezApi?.ezclocker?.ezLogger?.[level])) {
            if (EzObject.isValid(err)) {
                globalThis.ezApi.ezclocker.ezLogger[level](msg, err);
            } else {
                globalThis.ezApi.ezclocker.ezLogger[level](msg);
            }
        } else if (EzFunction.isFunction(globalThis?.console?.[level])) {
            if (EzObject.isValid(err)) {
                globalThis.console[level](msg, err);
            } else {
                globalThis.console[level](msg);
            }
        }
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the reference to the browser's Window instance that contains the DOM document.
     * Normally availabe from one or more of the following:
     * 1) globalThis.window
     * 1) window
     * @returns {Window}
     */
    static get ezWindow() {
        return globalThis?.window || EzObject.assignOrNull(window);
    }

    /**
     * @static
     * @public @readonly @property
     * Returns true if the current browser URL contains the query parameter 'debug=true', otherwise returns false.
     * @returns {boolean}
     */
    static get urlHasDebugFlag() {
        return EzUx.ezWindow?.location?.search?.includes('debug=true');
    }


    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window's Navigator interface that represents the state and the identity of the user agent. It allows scripts to query it
     * and to register themselves to carry on some activities.
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.navigator
     * 2) window.navigator
     * 3) globalThis.navigator
     * 4) navigator
     * @returns {Navigator}
     */
    static get ezNavigator() {
        return EzUx.ezWindow?.navigator || globalThis?.navigator || EzObject.assignOrNull(navigator);
    }

    /**
     * @static
     * @private @field
     * Stores a sudo-enum map storing HTML element tag (uppercase) = HTML element tag name (lowercase).
     * @type {object}
     */
    static #elementTagName = {
        GLOBALTHIS: 'globalThis',
        globalthis: 'globalThis',
        WINDOW: 'window',
        window: 'window',
        NAVIGATOR: 'navigator',
        navigator: 'navigator',
        SCREEN: 'screen',
        screen: 'screen',
        DOCUMENT: 'document',
        document: 'document',
        DOCTYPE: '!DOCTYPE',
        doctype: '!DOCTYPE',
        HTML: 'html',
        html: 'html',
        HEAD: 'head',
        head: 'head',
        META: 'meta',
        meta: 'meta',
        LINK: 'link',
        link: 'link',
        STYLE: 'style',
        style: 'style',
        SCRIPT: 'script',
        script: 'script',
        TITLE: 'title',
        title: 'title',
        BODY: 'body',
        body: 'body',
        DIV: 'div',
        div: 'div',
        A: 'a',
        a: 'a',
        INPUT: 'input',
        input: 'input',
        SPAN: 'span',
        span: 'span',
        LABEL: 'label',
        label: 'label',
        TABLE: 'table',
        table: 'table',
        TR: 'tr',
        tr: 'tr',
        TD: 'td',
        td: 'td',
        TH: 'th',
        th: 'th',
        UL: 'ul',
        ul: 'ul',
        LI: 'li',
        li: 'li',
        BUTTON: 'button',
        button: 'button',
        SELECT: 'select',
        select: 'select',
        OPTION: 'option',
        option: 'option',
        TEXTAREA: 'textarea',
        testarea: 'textarea',
        FORM: 'form',
        form: 'form',
        IMG: 'img',
        img: 'img',
        STRONG: 'strong',
        strong: 'strong'
    };
    /**
     * @static
     * @public @readonly @property
     * a sudo-enum map storing HTML element tag (uppercase) = HTML element tag name (lowercase).
     * @returns {object}
     */
    static get elementTagName() {
        return this.#elementTagName;
    }

    /**
     * @public @readonly @property
     * Gets the name of the <html> element: 'html'
     * @returns {string}
     * @deprecated
     * Migrate to using static property: EzUx.elementTagName.HTML
     */
    static get htmlTagName() {
        return EzUx.elementTagName.HTML;
    }

    /**
     * @public @readonly @property
     * Gets the name of the <head> element: 'head'
     * @returns {string}
     * @deprecated
     * Migrate to using static property: EzUx.elementTagName.HEAD
     */
    static get headTagName() {
        return EzUx.elementTagName.HEAD;
    }

    /**
     * @public @readonly @property
     * Gets the name of the <title> element: 'title'
     * @returns {string}
     * @deprecated
     * Migrate to using static property: EzUx.elementTagName.TITLE
     */
    static get titleTagName() {
        return EzUx.elementTagName.TITLE;
    }

    /**
     * @public @readonly @property
     * Gets the name of the <body> element: 'body'
     * @returns {string}
     * @deprecated
     * Migrate to using static property: EzUx.elementTagName.BODY
     */
    static get bodyTagName() {
        return EzUx.elementTagName.BODY;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the reference to the browser Window's screen
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.screen
     * 2) window.screen
     * 3) screen
     * @returns {Screen}
     */
    static get ezScreen() {
        return EzUx.ezWindow?.screen || EzObject.assignOrNull(screen);
    }


    /**
     * @static
     * @public @readonly @property
     * Gets the reference to the browser's HTMLDocment
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document
     * 2) window.document
     * 3) globalThis.document
     * 4) document
     * @returns {HTMLDocument}
     */
    static get ezDocument() {
        return EzUx.ezWindow?.document || globalThis?.document || EzObject.assignOrNull(document);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window HTMLDocument's <head> element.
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document.head
     * 2) window.document.head
     * 3) globalThis.document.head
     * 4) document.head
     * ... is it ready ezHead? ... :( SHAME on you for thinking THAT was funny ...
     * @returns {Element}
     */
    static get ezHead() {
        return EzObject.assignOrNull(EzUx.ezDocument?.head);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window HTMLDocument's <head> element.
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document.title
     * 2) window.document.title
     * 3) globalThis.document.title
     * 4) document.title
     * @returns {Element}
     */
    static get ezTitle() {
        return EzObject.assignOrNull(EzUx.ezDocument?.title);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the browser's Window HTMLDocument's <body> element.
     * Normally availabe from one or more of the following:
     * 1) globalThis.window.document.body
     * 2) window.document.body
     * 3) globalThis.document.body
     * 4) document.body
     * @returns {Element}
     */
    static get ezBody() {
        return EzObject.assignOrNull(EzUx.ezDocument?.body);
    }

    /**
     * @static
     *@public @function
     * 1) Gets the instance of the element who's name equals the provided globalElementNameOrInstance string
     * 2) Gets the provided globalElementNameOrInstance if the provided globalElementNameOrInstance
     *    is an instance of a known global element.
     * @returns {Element|null}
     * @deprecated
     * Migrate to: EzUx.globalElement(globalElementNameOrInstance)
     */
    static ezGlobalElement(globalElementNameOrInstance) {
        return EzUx.globalElement(globalElementNameOrInstance);
    }

    /**
     * @static
     *@public @function
     * 1) If the provided globalElementNameOrInstance is a string and matches a known global HTML element,
     *    then the global HTML element referece is returned.
     * 2) If the provided globalElementNameOrInstance is a instance of an HTML element who's name matches a known global HTML element,
     *    then the glboal HTML element reference is returned.
     * 3) Otherwise, null is returned.
     * @returns {Element|null}
     */
    static globalElement(globalHtmlElementNameOrGlobalHtmlElementRef) {
        if (!EzObject.isValid(globalHtmlElementNameOrGlobalHtmlElementRef)) {
            // No global element name or HTML element reference provided
            return null;
        }

        if (EzString.isString(globalHtmlElementNameOrGlobalHtmlElementRef)) {
            switch (globalHtmlElementNameOrGlobalHtmlElementRef.toLowerCase()) {
                case EzUx.elementTagName.WINDOW:
                    return EzUx.ezWindow;
                case EzUx.elementTagName.NAVIGATOR:
                    return EzUx.ezNavigator;
                case EzUx.elementTagName.SCREEN:
                    return EzUx.ezScreen;
                case EzUx.elementTagName.DOCUMENT:
                    return EzUx.ezDocument;
                case EzUx.elementTagName.BODY:
                    return EzUx.ezBody;
                case EzUx.elementTagName.HEAD:
                    return EzUx.ezHead;
                case EzUx.elementTagName.TITLE:
                    return EzUx.ezTitle;
                default:
                    return null;
            }
        }

        if (EzUx.ezWindow === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezWindow;
        }

        if (EzUx.ezNavigator === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezNavigator;
        }

        if (EzUx.ezScreen === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezScreen;
        }

        if (EzUx.ezDocument === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezDocument;
        }

        if (EzUx.ezBody === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezBody;
        }

        if (EzUx.ezHead === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezHead;
        }

        if (EzUx.ezTitle === globalHtmlElementNameOrGlobalHtmlElementRef) {
            return EzUx.ezTitle;
        }

        // Supported global element name or instance not found
        return null;
    }

    /**
     * @private @field
     * Stores a categorized map of CSS classes.
     * @returns {object}
     */
    static #ezCssClasses = {
        ezTextClasses: {
            'underlineHideAltKeyUp': 'ezText-underline-hide-alt-key-up',
            'underlineShowAltKeyDown': 'ezText-underline-show-alt-key-down'
        }
    }
    static get ezCssClasses() {
        return this.#ezCssClasses;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets constant css class name (from ez-text.css):  'ezText-underline-hide-alt-key-up'
     * @returns {string}
     */
    static get css_ezTextUnerlineHideAltKeyUp() {
        return EzUx.ezCssClasses.ezTextClasses.underlineHideAltKeyUp;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets constant css class name (from ez-text.css): 'ezText-underline-show-alt-key-down'
     * @returns {string}
     */
    static get css_ezTextUnderlineShowAltKeyDown() {
        return EzUx.ezCssClasses.ezTextClasses.underlineShowAltKeyDown;
    }

    /**
     * @static
     * @private @field
     * Stores an object of bulk actions functions
     * Available Bulk Actions:
     *  {
     *      hide: (...elementIds) => { ... }
     *      setDisplay: (elementIds, displayValue = EzString.EMPTY) => { ... }
     * }
     * @type {object}
     */
    static #bulkActions = {
        /**
         * @public @function
         * Hides the specified elements.
         * @param {...string} [elementIds = EzArray.EMPTY]
         * The IDs of the elements to hide.
         * Example usage: EzUx.bulkActions.hide('elementId1', 'elementId2', 'elementId3');
         * @returns {object}
         * A map of elementId = hidden or not for each elementId in elementIds.
         * If the element was successfully hidden, the value will be true. If the element could not be found or hidden, the value will be false.
         */
        hide: (...elementIds) => {
            if (!EzArray.hasLength(elementIds)) {
                return EzObject.EMPTY;
            }

            let bulkActionResults = EzObject.EMPTY;

            for (const elementId of elementIds) {
                bulkActionResults[elementId] = EzUx.hide(elementId, false);
            }

            return bulkActionResults;
        },
        /**
         * @public @function
         * Hides the specified elements.
         * Throws a EzBadParamException if any of the the element references in elementIds does not exist.
         * @param {...string} [elementIds = EzArray.EMPTY]
         * The IDs of the elements to hide.
         * Example usage: EzUx.bulkActions.hide('elementId1', 'elementId2', 'elementId3');
         * @returns {object}
         * A map of elementId = hidden or not for each elementId in elementIds.
         * If the element was successfully hidden, the value will be true. If the element could not be found or hidden, the value will be false.
         */
        hideOrThrow: (...elementIds) => {
            if (!EzArray.hasLength(elementIds)) {
                return EzObject.EMPTY;
            }

            let bulkActionResults = EzObject.EMPTY;

            for (const elementId of elementIds) {
                const element = EzUx.getElementForElementRef(
                    elementId,
                    'bulk hide elements',
                    false,
                    'elementId in elementIds',
                    'bulkActions.hideOrThrow(...elementIds)');
                if (EzObject.isValid(element)) {
                    bulkActionResults[elementId] = EzUx.hide(elementId, false);
                } else {
                    bulkActionResults[elementId] = false;
                }
            }

            return bulkActionResults;
        },
        /**
         * @public @static @function
         * Sets the display style of the specified elements to the provided display value.
         * @param {array} [elementIds = EzArray.EMPTY]
         * An array of element IDs to set the display style for.
         * @param {string} displayValue = EzArray.EMPTY]
         * The display value to set for the specified elements (e.g., 'block', 'inline', 'none', etc.).
         * Defaults to an empty string, which will remove any inline display style and allow the element to inherit its display value from CSS.
         * Example usage: EzUx.bulkActions.setDisplay(['elementId1', 'elementId2'], 'block');
         * @returns {object}
         * Returns a map of elementId = display value set or not for each elementId in elementIds.
         * If the provided elementIds array is empty, an empty object is returned.
         */
        setDisplay: (elementIds = EzArray.EMPTY, displayValue = EzString.EMPTY) => {
            if (!EzArray.hasLength(elementIds)) {
                return EzObject.EMPTY;
            }
            if (!EzArray.hasLength(displayValue)) {
                throw new EzBadParamException(
                    { displayValue },
                    'bulkactions.setDisplay(elementIds = EzArray.EMPTY, displayValue = EzString.EMPTY)');
            }
            if (elementIds.length !== displayValue.length) {
                throw new EzBadStateException(
                    'Expected param displayValue array length to be equal to the elementIds array length.',
                    ` However, displayValue length is ${displayValue.length} and elementIds length is ${elementIds.length}.`);
            }

            let bulkActionResults = EzObject.EMPTY;

            for (const elementId of elementIds) {
                const element = EzUx.findElement(elementId);

                if (EzObject.isValid(element)) {
                    element.style.display = displayValue;

                    bulkActionResults[elementId] = true;
                } else {
                    bulkActionResults[elementId] = false;
                }
            }

            return bulkActionResults;
        },
        /**
         * @public @static @function
         * Sets the display style of the specified elements to the provided display value.
         * @param {array} [elementIds = EzArray.EMPTY]
         * An array of element IDs to set the display style for.
         * @param {string} displayValue = EzArray.EMPTY]
         * The display value to set for the specified elements (e.g., 'block', 'inline', 'none', etc.).
         * Defaults to an empty string, which will remove any inline display style and allow the element to inherit its display value from CSS.
         * Example usage: EzUx.bulkActions.setDisplay(['elementId1', 'elementId2'], 'block');
         * @returns {object}
         * Returns a map of elementId = display value set or not for each elementId in elementIds.
         * If the provided elementIds array is empty, an empty object is returned.
         */
        setDisplayOrThrow: (elementIds = EzArray.EMPTY, displayValue = EzString.EMPTY) => {
            if (!EzArray.hasLength(elementIds)) {
                return EzObject.EMPTY;
            }
            if (!EzArray.hasLength(displayValue)) {
                throw new EzBadParamException(
                    { displayValue },
                    'bulkactions.setDisplayOrThrow(elementIds = EzArray.EMPTY, displayValue = EzString.EMPTY)');
            }
            if (elementIds.length !== displayValue.length) {
                throw new EzBadStateException(
                    'Expected param displayValue array length to be equal to the elementIds array length.',
                    ` However, displayValue length is ${displayValue.length} and elementIds length is ${elementIds.length}.`);
            }

            let bulkActionResults = EzObject.EMPTY;

            for (const elementId of elementIds) {

                const element = EzUx.getElementForElementRef(
                    elementId,
                    'bulk assign element display value',
                    false,
                    'elementId in elementIds',
                    'bulkActions.setDisplayOrThrow(elementIds = EzArray.EMPTY, displayValue = EzString.EMPTY)');

                if (EzObject.isValid(element)) {
                    element.style.display = displayValue;

                    bulkActionResults[elementId] = true;
                } else {
                    bulkActionResults[elementId] = false;
                }
            }

            return bulkActionResults;
        }
    };
    /**
     * @static
     * @public @readonly @property
     * Stores an object of bulk actions functions
     * Available Bulk Actions:
     *  {
     *      hide: (...elementIds) => { ... }
     *      setDisplay: (elementIds, displayValue = EzString.EMPTY) => { ... }
     * }
     * @type {object}
     */
    static get bulkActions() {
        return this.#bulkActions;
    }

    /**
     * @static
     *@public @function
     * Finds an HTML element by id, name, tag name, or class name.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezFindByElementOrId(elementOrId)
     *  globalThis.ezApi.ezclocker.ezElement(elementId)
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  globalThis.ezApi.ezclocker.ezUi.ezId$(selector)
     *  globalThis.ezApi.ezclocker.ezRef(elementRef)
     * @param {string} ezElementQuery
     * Default: null
     * Supports the following types for the elementQuery param:
     *  1) An HTML element reference
     *  2) An HTML element id string
     *  3) A jQuery HTML element reference (include the array of elements)
     *  4) A HTML element jQueryId string (aka '#id')
     *  5) HTML tag name
     *  6) HTML class name
     *  5) A jQuery selector
     * @returns {null|object}
     */
    static findElement(ezElementQuery = null) {
        if (!EzObject.isValid(ezElementQuery)) {
            // Undefined or null value for ezElementQuery, returning null result
            return null;
        }

        if (!EzString.isString(ezElementQuery)) {
            // Assuming to be an element reference
            return ezElementQuery;
        }

        // Find by global HTML element name or global HTML element reference,
        let htmlElement = EzUx.globalElement(ezElementQuery);

        if (EzObject.isValid(htmlElement)) {
            return htmlElement;
        }

        // Find by HTML element reference, or HTML element with matching id
        htmlElement = EzUx.findById(ezElementQuery);

        if (EzObject.isValid(htmlElement)) {
            return htmlElement;
        }

        // Find by HTML element's class name (returning first encountered only)
        let htmlElements = EzUx.findAllByClassName(ezElementQuery);

        if (EzNumber.ZERO !== htmlElements?.length) {
            return htmlElements[0];
        }

        // Find and return the first HTML element found via globalThis.document.getElementsByName(elementName)
        htmlElements = EzUx.findAllByName(ezElementQuery);

        if (EzNumber.ZERO !== htmlElements?.length) {
            return htmlElements.item(0);
        }

        // Find via jQuery id query
        let jQueryElementRef = EzUx.$id(ezElementQuery);

        if (EzObject.isValid(jQueryElementRef)) {
            return EzNumber.ZERO !== jQueryElementRef?.length
                ? jQueryElementRef[0]
                : null;
        }

        // Finally, try and find by jQuery selector
        jQueryElementRef = EzUx.$(ezElementQuery);

        return EzObject.isValid(jQueryElementRef) && EzNumber.ZERO !== jQueryElementRef?.length
            ? jQueryElementRef[EzNumber.FIRST_INDEX]
            : null;
    }

    /**
     * @static
     *@public @function
     * Finds the HTML element who's id matches the provided elementId
     * Makes the assumtion that all the HTML elements in the current document have unique ids and if the matching id is associated with more than one
     * HTML element only the first HTML element encountered is returned.
     * Returns null if no HTML element's id matches the provided elementId value (case sensitive)
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id) (for non-jQueryIds)
     *  globalThis.ezApi.ezclocker.ezElement(elementId) (for non-jQueryIds)
     * @param {string} elementId
     * @returns {null|object}
     */
    static findById(elementId) {
        if (!EzString.hasText(elementId)) {
            return null;
        }

        const globalElement = EzUx.globalElement(elementId);

        if (EzObject.isValid(globalElement)) {
            return globalElement;
        }

        // If the elementOrId is an object, assume it is an HTML element and return it.
        // Otherwise, look up the HTML element by id and return it.
        return EzObject.assignOrNull(
            globalThis.document.getElementById(elementId));
    }

    /**
     * @static
     * @public @function
     * Extracts the id of the provided HTML or jQuery element reference.
     * Case #1:
     * If the provided element reference is a jQuery element reference:
     * 1) The jQuery element array is iterated to find the first element that has a non-blank id and that id is returned.
     * 2) If the jQuery element array is empty, then a bad parameter error is thrown.
     * 3) If none of the elements in the jQuery element array have a non-blank id, then the first element in the array is assigned assigned a generated id
     * based upon the current date and time and the generated id is returned.
     * Case #2:
     * If the provided elementRef is a HTML node reference:
     * 1) If the HTML node reference has a non-blank id, then the id is returned.
     * 2) Otherwise, the HTML node reference's id is assigned a generated id based upon the current date and time and the generated id is returned..
     * @param {object} [elementRef=null]
     * @returns {string}
     */
    static extractElementIdFromElementRef(elementRef = null) {
        if (!EzObject.isValid(elementRef)) {
            throw new EzBadParamException(
                { elementRef },
                EzUx,
                EzUx.extractElementIdFromElementRef,
                'The elementRef param must be a valid HTML element reference or jQuery element reference.');
        }

        // Find the element's actual ID
        if (EzNumber.ZERO < elementRef?.length) {
            // Assumed to be a jQuery element reference pointing to one or more of HTML elements.

            // Obtain the id of the first element that has an id
            for (let index = EzNumber.ZERO;index < elementId.length;index++) {
                const elementRefId = elementRef?.[index]?.id;

                if (EzString.hasText(elementIdForElementRef)) {
                    return elementRefId;
                }
            }

            // Fall back to generating an id to use and returning that id

            elementRef[EzNumber.ZERO].id = EzUx.generateElementId();

            globalThis?.console?.warn(
                EzString.msg`
                    The provided jQuery element reference's array does not contain an element with a non-blank id.
                    Therefore, the first item in the jQuery element array is assigned the generated id of '${generatedId}' and the generated id is returned.`);

            elementRef[EzNumber.ZERO].id = generatedId;

            return elementRef[EzNumber.ZERO].id;
        }

        // Assumed to be a single HTML node/element referencce

        // Obtain the Id of the HTML element reference (if it has one)
        const elementRefId = elementRef?.id;

        if (EzString.hasText(elementRefId)) {
            return elementRefId;
        }

        // Fall back to generating an id to use and returning that id
        elementRef.id = EzUx.generateElementId();

        globalThis?.console?.warn(
            EzString.msg`
                The provided HTML node or jQuery element refernce ${elementId} does not have a valid, non-blank id.
                Therefore, the provided HTML node or jQuery element reference's id will get assigned the generated id of '${generatedId}' and the
                generated id is returned.`);

        return elementRef.id;
    }

    /**
     * @static
     * @public @function
     * Generates a HTML element id using the following template:
     * `${EzString.stringWithTextOrDefault(prefix, 'Ez')}_${EzString.stringWithTextOrDefault(name, EzDateTime.ezNowAsTimestamp())}_${EzString.stringWithTextOrDefault(suffix, 'Element')}`
     * @param {undefined|null|string} [prefix='Ez']
     * @param {undefined|null|string} [name=EzDateTime.ezNowAsTimestamp()]
     * @param {undefined|null|string} [suffix='Element']
     * @returns {string}
     */
    static generateElementId(prefix = 'Ez', name = EzDateTime.ezNowAsTimestamp(), suffix = 'Element') {
        return `${EzString.stringWithTextOrDefault(prefix, 'Ez')}` +
            EzChar.underscore +
            `${EzString.stringWithTextOrDefault(name, EzDateTime.ezNowAsTimestamp())}` +
            EzChar.underscore +
            `${EzString.stringWithTextOrDefault(suffix, 'Element')}`;
    }

    /**
     *@public @function
     * Attempts to determine the jQuery ID reference for the provided elementRefOrId value.
     * Throws EzBadParamException if the jQueryId could not be determined from the provided parameter value.
     * @param {object|string} elementIdOrRef
     * Expecting the value to be one of the following:
     *  1) An HTML element reference
     *  2) An HTML element id string
     *  3) A jQuery element reference (could be array of jQuery found elements)
     *  4) A jQuery id string with format: `#${element_id}`
     * @return {string}
     */
    static determineJQueryId(elementIdOrRef = EzString.EMPTY) {
        if ((EzString.isString(elementIdOrRef) && !EzString.hasText(elementIdOrRef)) ||
            (!EzObject.isValid(elementIdOrRef))) {
            throw new EzBadParamException(
                { elementIdOrRef },
                EzUx,
                EzUx.determineJQueryId,
                'A valid HTML element reference or string id of an existing HTML element is required.');
        }

        let jQueryId;

        if (EzString.isString(elementIdOrRef)) {
            // Assumed to be an HTML element id string.
            jQueryId = elementIdOrRef;
        } else {
            // If not a string, the elementId param is assumed to be an object reference to the element
            jQueryId = EzUx.extractElementIdFromElementRef(elementIdOrRef);
        }

        if (!EzString.hasText(jQueryId)) {
            throw new EzBadParamException(
                { elementIdOrRef },
                EzUx,
                EzUx.determineJQueryId,
                EzString.em`
                    Unable to determine the jQueryID from the provided elementRef_elementId_jQueryElementRef_jQueryId
                    param value due to the following error:
                    A valid HTML element reference, HTML element id, jQuery element reference, or jQuery element id selector is expected but was not found.
                    [elementId=${elementId}`);
        }

        return '#' === jQueryId[EzNumber.FIRST_INDEX]
            ? jQueryId
            : `#${jQueryId}`;
    }

    /**
     * @static
     *@public @function
     * Alias for EzUx.id$(elementId)
     * Alias for EzUx.$id(elementId)
     * Alias for EzUx.$(`#${elementId}`)
     * Returns a jQuery reference to the element with the provided id
     * @param {string} elementId
     * @deprecated
     * Migrate to one of the following:
     *  1) EzUx.ezId(elementId)
     *  2) EzUx.id(elementId)
     *  1) EzUx.$id(elementRefOrIdOrJQueryId)
     *  2) EzUx.id(elementId)
     *  3) EzUx.$(jQuerySelector)
     */
    static findJQueryElement(elementId) {
        return EzUx.$id(elementId);
    }

    /**
     * @static
     *@public @function
     * Alias for EzUx.findJQueryElement(jQuerySelector)
     * Alias for EzUx.$id(jQuerySelector)
     * Alias for EzUx.$(`#${elementId}`)
     * @param {string} jQuerySelector
     * A valid jQuery selector value
     * @returns {object|null}
     * @deprecated
     * Migrate to one of the following:
     *  1) EzUx.$id(elementRefOrIdOrJQueryId)
     *  2) ExUx.$(jQuerySelector)
     */
    static id$(elementId) {
        return EzUx.$id(elementId);
    }

    /**
     *@public @function
     * Alias for EzUx.findJQueryElement(id)
     * Alias for EzUx.id$(id)
     * Alias for EzUx.$(`#${elementId}`)
     * Gets a jQuery HTML element reference associated with the provided elementRefOrIdOrJQueryId param value.
     * Can replace:
     *  1) $(`#${elementId}) jQuery query for HTML element id
     *  1) globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  2) globalThis.ezApi.ezclocker.ezElement(elementId)
     *  3) globalThis.ezApi.ezId(id)
     *  4) globalThis.ezApi.ezId$(id)
     * @param {string} elementId
     * Supports the following value types:
     *  1) HTML element reference
     *  2) HTML element id string
     *  3) jQuery HTML element reference
     *  4) HTML element jQueryId string (aka '#id')
     */
    static $id(elementId) {
        try {
            return $(EzUx.determineJQueryId(elementId));
        } catch (err) {
            this.#log(
                EzString.em`
                    Unable to find an HTML element that is associated with the provided elementRefOrIdOrJQueryId param value
                    due to the following error: ${err.message}. Will return a null result instead.
                    [elementRefOrIdOrJQueryId: ${EzJson.toJson(elementId)}]`,
                'error',
                err);


            return null;
        }
    }

    /**
     * @static
     *@public @function
     * Alias for EzUx.findJQueryElement(id)
     * Alias for EzUx.id$(id)
     * Alias for EzUx.$id(id)
     * Finds an element via jQuery selector
     * Can replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId$(selector) (for jQuery element references, jQuery ids and selectors)
     *  globalThis.ezApi.ezclocker.ezRef(elementRef) (for jQuery element references, jQuery ids and selectors)
     *  globalThis.ezApi.ezclocker.ezId$(selector)
     *  globalThis.ezApi.ezclocker.ezRef(elementRef) (for jQuery element references, jQuery ids and selectors)
     *  globalThis.ezApi.ezclocker.ezId$(selector)
     *  globalThis.ezApi.ez$(jQuerySelector)
     * @returns {null|object}
     */
    static $(jQuerySelector) {
        if (!EzString.hasText(jQuerySelector) || !EzObject.isValid(jQuerySelector)) {
            this.#log(
                EzString.em`
                    A valid jQuery selector value is expected for the parameter jQuerySelector in call to ${EzUx.$}.
                    Will return a null result instead.`,
                'error');

            return null;
        }

        const jQueryElement = $(jQuerySelector);

        if (!EzObject.isValid(jQueryElement) || EzNumber.ZERO === jQueryElement?.length) {
            this.#log(
                EzString.em`
                    Unable to locate an HTML element for the provided jQuerySelector param
                    with value of ${EzJson.toJson(jQuerySelector)}
                    by using jQuery api call $(${jQuerySelector}).
                    Will return a null result instead.`);

            return null;
        }

        return jQueryElement;
    }

    /**
     * @static
     *@public @function
     * Gets the element assocaited with the provided elementId
     * Alias to EzUx.id - available to make switching from EzUi to EzUx easier.
     * Alias short-hand alternative to EzUx.findById
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  globalThis.ezApi.ezclocker.ezElement(elementId)
     *  globalThis.ezApi.ezId(id)
     *  globalThis.ezApi.ezId$(id)
     * @param {string} elementId
     */
    static ezId(elementId) {
        return EzUx.findById(elementId);
    }

    /**
     * @static
     *@public @function
     * Gets the element assocaited with the provided elementId
     * Alias to EzUx.ezId - available to make switching from EzUi to EzUx easier.
     * Alias Short-hand alternative to EzUx.findById
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezId(id)
     *  globalThis.ezApi.ezclocker.ezElement(elementId)
     *  globalThis.ezApi.ezId(id)
     *  globalThis.ezApi.ezId$(id)
     * @param {string} elementId
     */
    static id(elementId) {
        return EzUx.findById(elementId);
    }

    /**
     * @static
     *@public @function
     * Will return a live HTMLCollection of all elements which have all of the given class name(s).
     * @param {string} [classNames = EzString.EMPTY]
     * A string representing the class name(s) to match; multiple class names are separated by whitespace.
     * @returns {HTMLCollection}
     */
    static findAllByClassName(classNames = EzString.EMPTY) {
        if (!EzString.hasText(classNames)) {
            return EzArray.EMPTY;
        }

        return globalThis.document.getElementsByClassName(classNames);
    }

    /**
     * @static
     *@public @function
     * Will return a live HTMLCollection of found elements in the order they appear in the tree.
     * @param {string} [elementTagName = EzString.EMPTY]
     * The value of the name attribute of the element(s) to find
     * @returns {HTMLCollection}
     */
    static findAllByTagName(elementTagName = EzString.EMPTY) {
        if (!EzString.hasText(elementTagName)) {
            return EzArray.EMPTY;
        }

        return globalThis.document.getElementsByTagName(elementTagName);
    }

    /**
     * @static
     *@public @function
     * Will return a live NodeList collection of elements with a given name attribute in the document (if any)
     * Important: A live NodeList connection will automatically update as new elements with the same name are added to, or removed from,
     * the document.
     * @param {string} [elementName= EzString.EMPTY]
     * @returns {NodeList}
     */
    static findAllByName(elementName = EzString.EMPTY) {
        if (!EzString.hasText(elementName)) {
            return EzArray.EMPTY;
        }

        return globalThis.document.getElementsByName(elementName);
    }

    /**
     *@public @function
     * Gets a list of elements that match the provided aria attribute's value.
     * @param {string} ariaAttributeSuffix
     * String should not include the 'aria' prefix.
     * @param {string} value
     * @returns {array}
     */
    static findByAriaAttributeSuffix(ariaAttributeSuffix = null, value = null) {
        if (!EzString.hasText(ariaAttributeSuffix)) {
            throw new EzBadParamException(
                { ariaAttributeSuffix },
                EzUx,
                EzUx.findByAriaAttributeSuffix);
        }

        const fullAriaAttributeName = ariaAttributeSuffix.startsWith('aria-')
            ? ariaAttributeSuffix
            : `aria-${ariaAttributeSuffix}`;

        let strValue;

        if (EzObject.isValid(value)) {
            strValue = EzString.EMPTY;
        } else if (EzNumber.isNumber(value)) {
            strValue = EzNumber.strToNumber(value)
        } else if (EzString.isString(value)) {
            strValue = value;
        } else {
            strValue = value.toString();
        }

        return globalThis.document.querySelectorAll(`[${fullAriaAttributeName}="${strValue}"]`);
    }

    /**
     * @static
     *@public @function
     * Determines if the element associated with the provided elementRef is visible (display != 'none')
     * @returns {boolean}
     */
    static isVisible(elementRef) {
        return EzObject.isValid(
            EzUx.getElementIfVisible(elementRef));
    }

    /**
     * @static
     *@public @function
     * Gets the HTML element associated with the provided elementRef if it is visible (aka display != 'none').
     * Otherwise, returns null.
     * @param {string} elementRef
     * @returns {null|object}
     */
    static getElementIfVisible(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!element) {
            return null;
        }

        return 'none' !== element.style.display
            ? element
            : null;
    }

    /**
     * @static
     *@public @function
     * Gets the HTML element associated with the provided elementRef if it is NOT visible (aka display:none).
     * Returns null if the element is visible OR does not exist.
     * @param {string} elementRef
     * @returns {null|object}
     */
    static getElementIfNotVisible(elementRef) {
        const element = EzUx.findElement(elementRef);

        return 'none' === element?.style?.display
            ? element
            : null;
    }

    /**
     * @static
     *@public @function
     * Removes all of the ezClocker legacy 'hidden' classes (if any) from the element.
     * If a legacy 'hide' class is detected on the element, the element's display is set to none.
     * Legacy 'hide' classes that get removed:
     * 1) ezHiddenByDefault
     * 2) ezHidden
     * 3) ezHiddenDefault
     * @param {string} elementRef
     * @returns {null|object}}
     * Returns a referece to the element if it exists, null otherwise.
     */
    static removeLegacyHiddenClasses(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            // No element to convert classes on
            return null;
        }

        if (element.classList.contains('ezHiddenByDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenByDefault');
        }

        if (element.classList.contains('ezHidden')) {
            element.style.display = 'none';

            element.classList.remove('ezHidden');
        }

        if (element.classList.contains('ezHiddenDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenDefault');
        }

        return element;
    }

    /**
     * @protected @method
     * Obtains the HTML element associated with the provided elementRef.
     * Throws Error if:
     * 1) Provided elementRef is undefined or null
     * 2) No html element exists for the elementRef
     * @param {string} elementRef
     * ID of the HTML element
     * @param {undefined|null|string} pendingAction
     * Dfeault: EzUx.getElementForElementRef.toString();
     * @param {undefined|null|boolean} throwOnError
     * Default: true
     * @returns {object}
     * Returns the HTML element associated with the provided elementRef
     */
    static getElementForElementRef(elementRef = null, action = EzString.EMPTY, throwOnError = true, paramName = 'elementRef',
        methodName = 'getElementForElementRef') {

        if (!EzString.hasText(elementRef)) {
            const em = EzString.em`
                Cannot perform '${EzString.stringWithTextOrDefault(action, 'an action')}'
                on one or more HTML elements due to the following error: The element referenced by ${elementRef} is undefined or null (does not exist).`;

            this.#log('error', em)

            if (!EzBoolean.isTrue(throwOnError)) {
                throw new EzBadParamException(
                    EzString.stringWithTextOrDefault(
                        paramName,
                        'elemenRef'),
                    this,
                    EzString.stringWithTextOrDefault(
                        methodName,
                        'methodName'),
                    em);
            }
        }

        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            const em = EzString.em`
                Cannot perform '${EzString.stringWithTextOrDefault(action, 'an action')}'
                on one more more HTML elements due to the following error:
                The element referece '${elementRef}' is not associated with any HTML element in the current document (element does not exist).`;

            if (!EzBoolean.isTrue(throwOnError)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    EzString.stringWithTextOrDefault(paramName, 'elemenRef'),
                    this,
                    EzString.stringWithTextOrDefault(methodName, 'methodName'),
                    em);
            } else {
                this.#log(em, 'warn');
            }
        }

        return element;
    }

    /**
     * @static
     *@public @function
     * Applies the provdied Animate.style (animate.css) style to the HTML element associated with the provided elementRef.
     * @param {string} elementRef
     * Id of the HTML element to apply the animate.css class to
     * @param {string} animateStyleCssClass
     * @param {undefined|null|boolean} [throwOnError=true]
     */
    static animate(elementRef, animateStyleCssClass, throwOnError = true) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            'animate',
            throwOnError);

        if (!EzObject.isValid(element)) {
            return;
        }

        if (!EzString.hasText(animateStyleCssClass)) {
            const em = `Cannot execute 'animate' on HTML element reference ${elementRef}: Animation class is undefined or null.`;

            if (EzBoolean.isTrue(throwOnError)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    'animateStyleCssClass',
                    EzUx,
                    EzUx.animate,
                    em);
            } else {
                this.#log(em, 'warn');
            }

            return;
        }

        if (!animateStyleCssClass.startsWith('animate__')) {
            const em = EzString.em`
                Cannot animate element ${elementRef}: Animate.style animate.css classes must start with 'animate__'.
                For more information see: https://animate.style/`;

            if (EzBoolean.isTrue(throwOnError)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    'animateStyleCssClass',
                    EzUx,
                    EzUx.animate,
                    em);
            } else {
                this.#log(em, 'warn');
            }

            return;
        }

        EzUx.addCssClass(
            element,
            'animate__animated');

        EzUx.addCssClass(
            element,
            animateStyleCssClass);
    }

    /**
     * @static
     *@public @function
     * Shows the HTML element asscoaited with the provdied elementRef using the fade-in anumation.
     * @param {string} elementRef
     * Id of the HTML element to apply the animate.css class to
     */
    static fadeIn(elementRef) {
        if (!EzObject.isValid(EzUx.getElementForElementRef(elementRef, 'fadeIn', false))) {
            return;
        }

        EzUx.animate(
            elementRef,
            'animate__fadeIn');

        EzUx.show(
            elementRef,
            false);
    }

    /**
     * @static
     *@public @function
     * Shows the HTML element asscoaited with the provdied elementRef using the fade-in anumation.
     * @param {string} elementRef
     * Id of the HTML element to apply the animate.css class to
     */
    static fadeOut(elementRef) {
        if (!EzObject.isValid(EzUx.getElementForElementRef(elementRef, 'fadeOut', false))) {
            return;
        }

        EzUx.animate(
            elementRef,
            'animate__fadeOut');

        EzUx.hide(
            elementRef,
            false);
    }

    /**
     * @static
     * @public @function
     * Sets the content of the element associated with the provided elementRef and shows the element.
     * Calls:
     *  1) EzUx.setContent(elementRef, content)
     *  2) EzUx.show(elementRef, optionalDisplayStyleOrFadeIn, fadeIn)
     */
    static setContentAndShow(elementRef, content = EzString.EMPTY, optionalDisplayStyleOrFadeIn = null, fadeIn = false) {
        EzUx.setContent(
            elementRef,
            content);

        EzUx.show(
            elementRef,
            optionalDisplayStyleOrFadeIn,
            fadeIn);
    }

    /**
     * @static
     * @public @function
     * Shows the element associated by elementRef and then enabled the element by removing the 'disabled' attribute and 'disabled' CSS class (if either exists).
     * @param {string} elementRef
     * @param {undefined|null|string} optionalDisplayStyleOrFadeInBoolean
     * @param {undefined|null|boolean} fadeIn
     */
    static showEnabled(elementRef, optionalDisplayStyleOrFadeInBoolean = null, fadeIn = false) {
        if (!EzString.hasText(elementRef)) {
            throw new EzBadParamException(
                'elementRef',
                EzUx,
                EzUx.showEnabled);
        }

        EzUx.show(
            elementRef,
            optionalDisplayStyleOrFadeInBoolean,
            fadeIn);

        EzUx.enable(elementRef)
    }

    /**
     * @static
     * @public @function
     * Shows all the elements associated with the provided elementRefs and then enables the element by removing the 'disabled' attribute and 'disabled'
     * CSS class (if either exists).
     * @param {undefined|null|array} elementRefs
     * Default: EzArray.EMPTY
     * @param {undefined|null|array} optionalDisplayStyleOrFadeIns
     * Default: EzArray.EMPTY
     * @param {undefined|null|array} fadeIns
     * Default: EzArray.EMPTY
     */
    static showEnabledAll(elementRefs = EzArray.EMPTY, optionalDisplayStylesOrFadeInBooleans = EzArray.EMPTY, fadeIns = EzArray.EMPTY) {
        if (!EzArray.hasLength(elementRefs)) {
            // Nothing to show or enable
            return;
        }

        for (let index = 0;index < elementRefs.length;index++) {
            const elementRef = EzObject.assignOrNull(elementRefs?.[index]);

            if (EzObject.isValid(elementRef)) {
                EzUx.showEnabled(
                    elementRef,
                    EzArray.hasLength(optionalDisplayStylesOrFadeInBooleans) && index < optionalDisplayStylesOrFadeInBooleans.length
                        ? EzObject.assignOrNull(optionalDisplayStylesOrFadeInBooleans?.[index])
                        : null,
                    EzArray.hasLength(fadeIns) && index < fadeIns.length
                        ? EzBoolean.booleanOrFalse(fadeIns?.[index])
                        : false);
            }
        }
    }

    /**
     * @static
     * @public @function
     * Hides the element associated by elementRef and then disables the element by adding the 'disabled' attribute and 'disabled' CSS class
     * (if either does not exist).
     * @param {string} elementRef
     * @param {undefined|null|boolean} fadeOut
     */
    static hideDisabled(elementRef, fadeOut = false) {
        EzUx.hide(elementRef, fadeOut);

        EzUx.disable(elementRef)
    }

    /**
     * @static
     * @public @function
     * Hides all the elements associated by the provided elementRefs and then removes the elements from the DOM.
     * @param {array} elementRefs
     * Array of element references (ids)
     * Default: EzArray.EMPTY
     * @param {array} fadeOuts
     * Array of boolean values indicating whether to apply the fadeOut animation when hiding each element (
     * if the value is true, the fadeOut animation will be applied when hiding the element).
     * Index in this array corresponds to the elementRef at the same index in the elementRefs array.
     * If no value at the elementRefs index, then the default value of false is used.
     * Default: EzArray.EMPTY
     */
    static hideDisabledAll(elementRefs = EzArray.EMPTY, fadeOuts = EzArray.EMPTY) {
        if (!EzArray.hasLength(elementRefs)) {
            // Nothing to hide
            return;
        }

        for (let index = 0;index < elementRefs.length;index++) {
            const elementRef = EzObject.assignOrNull(elementRefs?.[index]);

            if (EzObject.isValid(elementRef)) {
                EzUx.hideDisabled(
                    elementRef,
                    EzArray.hasLength(fadeOuts) && index < fadeOuts.length
                        ? EzBoolean.booleanOrFalse(fadeOuts?.[index])
                        : false);
            }
        }
    }


    /**
     * @static
     * @public @function
     * Hides the element associated by elementRef and then removes the element from the DOM.
     * @param {string} elementRef
     */
    static hideRemove(elementRef, fadeOut = false) {
        EzUx.hide(elementRef, fadeOut);

        EzUx.remove(elementRef);
    }

    /**
     * @static
     * @public @function
     * Hides all the elements associated by the provided elementRefs and then removes the elements from the DOM.
     * @param {array} elementRefs
     * @param {array} fadeOuts
     */
    static hideRemoveAll(elementRefs = EzArray.EMPTY, fadeOuts = EzArray.EMPTY) {
        if (!EzArray.hasLength(elementRefs)) {
            // Nothing to hide
            return;
        }

        for (let index = 0;index < elementRefs.length;index++) {
            const elementRef = EzObject.assignOrNull(elementRefs?.[index]);

            if (EzObject.isValid(elementRef)) {
                EzUx.hideDisabled(
                    elementRef,
                    EzArray.hasLength(fadeOuts) && index < fadeOuts.length
                        ? EzBoolean.booleanOrFalse(fadeOuts?.[index])
                        : false);
            }
        }
    }

    /**
     * @static
     *@public @function
     * Shows the element or element associated by the provided id
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezShowElement(elementOrId, optionalDisplayStyle)
     *  globalThis.ezApi.ezclocker.ezUi.ezShow(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezShow$(selector)
     * @param {string} elementRef
     * @param {undefined|null|string} optionalDisplayStyle
     * @param {undefined|null|boolean} [fadeIn = false]
     * @param {undefined|null|boolean} [throwOnError=true]
     * Defaults:
     *  If element is a div then default is 'block'
     *  If element is a table then default is 'table'
     *  Otherwise, the default is 'initial'
     */
    static show(elementRef, optionalDisplayStyleOrFadeIn = null, fadeIn = false, throwOnError = true) {
        if (!EzObject.isValid(elementRef)) {
            if (EzBoolean.booleanOrTrue(throwOnError)) {
                throw new EzBadParamException(
                    'elementRef',
                    EzUx,
                    EzUx.show);
            }

            return false;
        }

        if (EzObject.isValid(optionalDisplayStyleOrFadeIn)) {
            if (EzBoolean.isBoolean(optionalDisplayStyleOrFadeIn)) {
                fadeIn = optionalDisplayStyleOrFadeIn;
            } else if (!EzString.isString(optionalDisplayStyleOrFadeIn)) {
                optionalDisplayStyleOrFadeIn = EzString.EMPTY;
            }
        }

        const element = EzUx.getElementForElementRef(
            elementRef,
            'show',
            true);

        if (!element) {
            return;
        }

        if (EzBoolean.isTrue(fadeIn)) {
            EzUx.fadeIn(elementRef);

            return;
        }

        const displayStyle = globalThis.getComputedStyle(element, null).display;

        if ('none' !== displayStyle) {
            // already visible
            return;
        }

        if (element.classList.contains('ezHiddenByDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenByDefault');
        }

        if (element.classList.contains('ezHidden')) {
            element.style.display = 'none';

            element.classList.remove('ezHidden');
        }

        if (element.classList.contains('ezHiddenDefault')) {
            element.style.display = 'none';

            element.classList.remove('ezHiddenDefault');
        }

        const upperCaseElementTagName = element.tagName.toUpperCase();

        if (EzString.hasText(optionalDisplayStyleOrFadeIn) && 'initial' !== optionalDisplayStyleOrFadeIn) {
            element.style.display = optionalDisplayStyleOrFadeIn;
        } else if (EzString.hasText(element?.ezDisplay)) {
            element.style.display = element.ezDisplay;
        } else if ('DIV' === upperCaseElementTagName) {
            element.style.display = 'block';
        } else if ('TABLE' === upperCaseElementTagName) {
            element.style.display = 'table';
        } else if ('TD' === upperCaseElementTagName) {
            element.style.display = 'table-cell';
        } else {
            element.style.display = 'unset';
        }

        // check what the display style is. if it is none, we set it to the user preferred display, as a last resort.
        if (EzString.hasText(optionalDisplayStyleOrFadeIn) && 'none' === globalThis?.getComputedStyle(element, null)?.display) {
            element.style.display = optionalDisplayStyleOrFadeIn;
        } else {
            // Otherwise, set it to '' and hope for the best :)
            element.style.display = EzString.EMPTY;
        }
    }

    /**
     * @static
     *@public @function
     * Shows the elements associated by the elementRefs.
     * @param {array} elementRef
     * Array of element references or element ids
     * Default: EzArray.EMPTY
     * @param {array} optionalDisplayStylesOrFadeIns
     * Array of display styles or fadeIn booleans to apply when showing each element.
     * Default: Arrray.EMPTY
     * Default display styles if not provided:
     *  If element is a div then default is 'block'
     *  If element is a table then default is 'table'
     *  Otherwise, the default is 'initial'
     * @param {array} fadeIns
     * Array of boolean values indicating whether to apply the fadeIn animation when showing each element
     * (if the value is true, the fadeIn animation will be applied when showing the element).
     */
    static showAll(elementRefs = EzArray.EMPTY, optionalDisplayStylesOrFadeIns = EzArray.EMPTY, fadeIns = EzArray.EMPTY) {
        if (!EzArray.hasLength(elementRefs)) {
            // Nothing to show
            return;
        }

        for (let index = 0;index < elementRefs.length;index++) {
            const elementRef = EzObject.assignOrNull(elementRefs?.[index]);

            if (EzObject.isValid(elementRef)) {
                EzUx.show(
                    elementRef,
                    EzArray.hasLength(optionalDisplayStylesOrFadeIns) && index < optionalDisplayStylesOrFadeIns.length
                        ? EzObject.assignOrNull(optionalDisplayStylesOrFadeIns?.[index])
                        : null,
                    EzArray.hasLength(fadeIns) && index < fadeIns.length
                        ? EzBoolean.booleanOrFalse(fadeIns?.[index])
                        : null);
            }
        }
    }

    /**
     * @static
     *@public @function
     * Hides the element or associated by the provided id
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezHideElement(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezHide(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezHide$(selector)
     *  globalThis.ezApi.ezclocker.ezUi.ezLongFadeOut(id, cb)
     *  globalThis.ezApi.ezclocker.ezUi.ezLongFadeOut$(selector, cb)
     *  globalThis.ezApi.ezclocker.ezUi.ezFadeOut(id, cb)
     *  globalThis.ezApi.ezclocker.ezUi.ezFadeOut$(selector, cb)
     *  globalThis.ezApi.ezclocker.ezUi.ezSlowFadeOut(id, cb)
     *  globalThis.ezApi.ezclocker.ezUi.ezSlowFadeOut$(selector, cb)
     * @param {string} elementRef
     */
    static hide(elementRef, fadeOut = false) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.hide.toString());

        if (null == element) {
            return;
        }

        if (EzBoolean.isTrue(fadeOut)) {
            EzUx.fadeOut(elementRef);

            return;
        }

        // getComputerStyle(...) is a global Javascript function available on globalThis
        const displayStyle = globalThis.getComputedStyle(element, null).display;

        if ('none' === displayStyle) {
            // Already hidden
            return;
        }

        element.ezDisplay = displayStyle;

        element.style.display = 'none';
    }


    /**
     * @static
     * @public @function
     * Hides all elements associated with the provided elementRefs
     * @param {array} elementRefs
     * Array of element references or element ids
     * Default: EzArray.EMPTY
     * @param {array} fadeOuts
     * Array of boolean values indicating whether to apply the fadeOut animation when hiding each element
     * (if the value is true, the fadeOut animation will be applied when hiding the element).
     * Index in this array corresponds to the elementRef at the same index in the elementRefs array.
     * If no value at the elementRefs index, then the default value of false is used.
     * Default: EzArray.EMPTY
     */
    static hideAll(elementRefs = EzArray.EMPTY, fadeOuts = EzArray.EMPTY) {
        if (!EzArray.hasLength(elementRefs)) {
            // Nothing to show
            return;
        }

        for (let index = 0;index < elementRefs.length;index++) {
            const elementRef = EzObject.assignOrNull(elementRefs?.[index]);

            if (EzObject.isValid(elementRef)) {
                EzUx.hide(
                    elementRef,
                    EzArray.hasLength(fadeOuts) && index < fadeOuts.length
                        ? EzBoolean.booleanOrFalse(fadeOuts?.[index])
                        : null);
            }
        }
    }

    /**
     * @static
     *@public @function
     * Gets the innerHtml content for the element associated with the provided elementRef
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezGetElementContent(elementOrId)
     * @param {string} elementRef
     * @returns {string}
     */
    static getContent(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot get element content for ${elementRef}: Element does not exist.`, 'error');

            return null;
        }

        return element.innerHTML;
    }

    /**
     * @static
     *@public @function
     * Sets the element's innerHtml
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetContent(id, innerHtmlContent)
     *  globalThis.ezApi.ezclocker.ezUi.ezContent(elementOrId, html)
     * @param {string} elementRef
     * @param {undefined|null|string} innerHTMLContent
     * Default: EzString.EMPTY
     */
    static setContent(elementRef, innerHTMLContent = EzString.EMPTY) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot get element content for ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        element.innerHTML = EzString.stringOrEmpty(innerHTMLContent);
    }

    /**
     * @static
     *@public @function
     * Clears the element's innerHtml content by assigning EzString.EMPTY
     * Replaces:
     *  1) globalThis.ezApi.ezclocker.ezUi.ezClearContent(id, innerHtmlContent)
     * @param {string} elementRef
     */
    static clearContent(elementRef) {
        return EzUx.setContent(elementRef, EzString.EMPTY);
    }

    /**
     * @static
     *@public @function
     * Clears the element's innerHtml content by assigning EzString.EMPTY
     * Replaces:
     *  1) globalThis.ezApi.ezclocker.ezUi.ezClearContent(id, innerHtmlContent)
     * @param {string} elementRef
     */
    static clearContentAndHide(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setCoclearContentAndHidentent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot clear element content and hide for elementRef=${elementRef}: Element does not exist.`, 'error');

            return;
        }

        EzUx.setContent(
            elementRef,
            EzString.EMPTY);

        EzUx.hide(elementRef);
    }


    /**
     * @static
     *@public @function
     * Prepends the provided prependHtmlContent to the beginning of the
     * content of the innerHTML
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezPrependContent(elementOrId, appendHtml)
     * @param {string} elementRef
     * @param {undefined|null|string} prependHtmlContent
     */
    static prependContent(elementRef, prependHtmlContent) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzString.hasLength(prependHtmlContent)) {
            return;
        }

        element.insertAdjacentHTML(
            'afterbegin',
            prependHtmlContent);
    }

    /**
     * @static
     *@public @function
     * Appends the provided appendHtml to the innerHTML property of the provided element or element associated with the
     * provided id.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendContent(elementOrId, appendHtml)
     *  globalThis.ezApi.ezclocker.ezUi.ezAppend(id, html)
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendHtml(elementOrId, appendHtml)
     * @param {string} elementOrId
     * @param {undefined|null|string} appendHtmlContent
     */
    static appendContent(elementRef, appendHtmlContent = EzString.EMPTY) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzString.hasLength(appendHtmlContent)) {
            return;
        }

        element.insertAdjacentHTML(
            'beforeend',
            appendHtmlContent);

        /*
        element.innerHTML = EzHtml.build`
            ${EzString.stringOrEmpty(element?.innerHtml)}
            ${appendHtmlContent}`;
        */
    }

    /**
     * @static
     *@public @function
     * Appends the provided appendHtmlContent to innerHtml property of the element identified by the jquerySelector.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendContent$
     *  globalThis.ezApi.ezclocker.ezUi.ezAppend$(selector, appendHtml)
     *  globalThis.ezApi.ezclocker.ezUi.ezAppendHtml$(selector, appendHtml)
     * @param {string} jquerySelector
     * @param {undefined|null|appendHtmlContent}
     */
    static appendContent$(jquerySelector, appendHtmlContent = EzString.EMPTY) {
        const element = EzUx.getElementForElementRef(
            jquerySelector,
            EzUx.setContent.toString(),
            true);

        if (EzString.hasLength(jquerySelector)) {
            this.#log('Cannot append content to element selected with an undefined or null jQuery selector.', 'error');
        }

        element.append(appendHtmlContent);
    }

    /**
     * @static
     *@public @function
     * Determines if the element with provided ID exists
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ez  sts(id)
     *  globalThis.ezApi.ezclocker.ezUi.ezDoesElementExist(elementOrId)
     * @returns {boolean}
     */
    static elementExists(elementRef) {
        const element = EzUx.findElement(elementRef);

        return EzObject.isValid(element);
    }

    /**
     * @static
     *@public @function
     * Determines if the element with provided ID exists
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezElementExists$(jquerySelector)
     * @param {string} jquerySelector
     * @returns {boolean}
     */
    static elementExists$(jquerySelector) {
        if (!EzString.hasText(jquerySelector)) {
            return false;
        }

        const element = EzUx.$(jquerySelector);

        return EzObject.isValid(element) && 0 !== element.length;
    }

    /**
     * @static
     *@public @function
     * Sets focus on the element referenced by the jquery selector
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezFocusElement(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezFocusInput(id)
     *  globalThis.ezApi.ezclocker.ezUi.ezFocus(elementId)
     * @param {selector} selector
     * @param {undefined|null|boolean} [throwOnError=true]
     * @returns {boolean}
     * True if the focus was called on the element, false otherwise.
     */
    static focus(elementRef, throwOnError = true) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString());

        if (!EzObject.isValid(element)) {
            const em = `Cannot focus an element selected by a null or undefined reference: ${elementRef}`;

            if (EzBoolean.booleanOrTrue(throwOnError)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    elementRef,
                    EzUx,
                    EzUx.focus,
                    em);
            } else {
                this.#log(em, 'warn');
            }

            return false;
        }

        element.focus();

        return true;
    }

    /**
     * @static
     *@public @function
     * Sets focus to the element referenced by the provided selector
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezFocus$(selector)
     *  globalThis.ezApi.ezclocker.ezUi.ezFocusInput$(selector)
     * @param {string} jquerySelector
     * @returns {null|object}
     */
    static focus$(jquerySelector) {
        if (!EzString.hasText(jquerySelector)) {
            this.#log('Cannot focus an element selected by a null or undefiend jquery selector.', 'error');

            return null;
        }

        const element = EzUx.getElementForElementRef(
            jquerySelector,
            EzUx.setContent.toString(),
            true);

        return element.focus();
    }

    /**
     * @static
     *@public @function
     * Sets the input value
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputValue(elementOrId, value)
     * @param {string} elementRef
     * @param {undefined|null|number|string|boolean} value
     * If the value is undefined or null then the element.value is assigned null.
     * @param {undefined|null|boolean} [throwOnError=false]
     */
    static setInputValue(elementRef, value, throwOnError = false) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setInputValue,
            true);

        if (!EzObject.isValid(element)) {
            const em = `Cannot set input value for element ${elementRef}: Element does not exist.`;

            if (EzBoolean.booleanOrTrue(throwOnError)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    elementRef,
                    EzUx,
                    EzUx.setInputValue,
                    em);
            } else {
                this.#log(em, 'warn');
            }

            return;
        }

        element.value = EzObject.assignOrNull(value);
    }

    /**
     * @static
     *@public @function
     * Sets the input value
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputValue(elementOrId, value)
     * @param {string} elementRef
     * @param {undefined|null|boolean} [throwOnError=false]
     */
    static clearInputValue(elementRef, throwOnError = false) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            const em = `Cannot clear input value for element ${elementRef}: Element does not exist.`;

            if (EzBoolean.booleanOrTrue(throwOnError)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    elementRef,
                    EzUx,
                    EzUx.setInputValue,
                    em);
            } else {
                this.#log(em, 'warn');
            }

            return;
        }

        element.value = EzString.EMPTY;
    }

    /**
     * @static
     *@public @function
     * Clears all the HTML input elements who's reference/id appear in the provided inputRefsArray.
     * @param {array} inputRefsArray
     */
    static clearAllInputValues(inputRefsArray = []) {
        if (!EzArray.hasLength(inputRefsArray)) {
            return;
        }

        inputRefsArray.forEach(
            (inputRef) => EzUx.clearInputValue(inputRef));
    }

    /**
     * @static
     *@public @function
     * Sets the input value for the item identified by the selector
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputValue$(selector, value)
     * @param {string} jquerySelector
     * @param {undefined|null|*} value
     */
    static setInputValue$(jquerySelect, value) {
        if (!EzString.hasText(jquerySelect)) {
            this.#log('Cannot set the input using a null or undefiend jquery selector value.', 'error');

            return;
        }

        const element = EzUx.getElementForElementRef(
            jquerySelect,
            EzUx.setContent.toString(),
            true);

        if (EzObject.isValid(element)) {
            element.val(
                EzObject.assignOrNull(value));
        }
    }

    /**
     * @static
     *@public @function
     * Gets the value the HTML input element referenced by the provided elementRef
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezGetInputValue(elementOrId)
     * @param {string} elementRef
     * @returns {*}
     * Returns the value of the input assoicated with the provided elementRef
     */
    static getInputValue(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzObject.assignOrNull(element?.value);
    }

    /**
     * @static
     * @public @function
     * Gets the value the HTML input element referenced by the provided elementRef
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezGetTrimInputValue(elementOrId)
     * @param {string} elementRef
     * @returns {*}
     * Returns the value of the input assoicated with the provided elementRef
     */
    static getTrimmedInputValue(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzString.stringWithTextOrEmpty(element?.value).trim();
    }


    /**
     * @static
     *@public @function
     * Removes the error highlight to an input editor
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezHideInputError(elementOrId)
     * @param {string} elementRef
     */
    static hideInputError(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot hide input error for element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        // Class ezInputErrorHighlight is located in ez-inputs.css
        if (element.classList.contains('ezInputErrorHighlight')) {
            element.classList.remove('ezInputErrorHighlight');
        }
    }


    /**
     * @public @function
     * Removes the error highlight to an input editor
     * @param {string} elementRef
     */
    static hideInputWarning(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot hide input warning for element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        if (element.classList.contains('ezInputWarningHighlight')) {
            element.classList.remove('ezInputWarningHighlight');
        }
    }

    /**
     * @static
     * @public @function
     * Determines if the provided element reference is the browser window.
     * @param {undefined|null|object} element
     * @returns {boolean}
     */
    static isElementWindow(element) {
        return EzObject.isValid(element) && '[object Window]' === EzObject.getObjectType(element);
    }

    /**
     * @static
     * @public @function
     * Gets the clientHeight of an element or the outerHeight of the window
     * @param {string} elementRef
     * @returns {number}
     */
    static clientHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            return 0;
        }

        return element.constructor.name === EzUx.ezWindow?.constructor?.name
            ? window.outerHeight
            : element.clientHeight;
    }

    /**
     * @static
     *@public @function
     * Gets the clientWidth of an element or the outerWidth of the window
     * @param {string} elementRef
     * @returns {number}
     * Returns the element if found, or null.
     */
    static clientWidth(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        if (!EzObject.isValid(element)) {
            return 0;
        }

        return element.constructor.name === EzUx.ezWindow?.constructor?.name
            ? window.outerWidth
            : element.clientWidth;
    }

    /**
     * @static
     *@public @function
     * Gets an element's offsetHeight or the window's outerHeight value.
     * Returns zero if the element does not exist or of the value obtained from the window or element is not a number.
     * The inner height value is a sum of the element or window:
     *  1) Height
     *  2) Padding top value
     *  3) Padding bottom value
     *  4) Border top size value
     *  5) Border bottom size value
     * @param {string} elementRef
     * @returns {number}
     */
    static outerHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window?.outerHeight)
            : EzNumber.numberOrZero(element?.offsetHeight);
    }

    /**
     * @static
     *@public @function
     * Gets an element's offsetWidth or the window's outerWidth value.
     * Returns zero if the element does not exist or of the value obtained from the window or element is not a number.
     * The value returned is the sum of the following element or window properties:
     *  1) Element height (without padding or border)
     *  2) Padding left value
     *  3) Padding right value
     *  4) Border left size value
     *  5) Border right size value
     * @param {string} elementRef
     * @returns {number}
     */
    static outerWidth(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window?.outerWidth)
            : EzNumber.numberOrZero(element?.offsetWidth);
    }

    /**
     * @static
     *@public @function
     * Gets an element's clientHeight or the window's innerHeight value.
     * Returns zero if the element/window does not exist or of the value obtained from the window or element is not a number.
     * The value returned is the height of the element or window without padding or border.
     * @param {string} elementRef
     * @returns {number}
     */
    static innerHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window.innerHeight)
            : EzNumber.numberOrZero(element.clientHeight);
    }

    /**
     * @static
     *@public @function
     * Gets an element's clientWidth or the window's innerWidth value.
     * Returns zero if the element/window does not exist or of the value obtained from the window or element is not a number.
     * The value returned is the width of the element or window without padding or border.
     * @param {string} elementRef
     * @returns {number}
     */
    static innerWidth(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        return EzUx.isElementWindow(element)
            ? EzNumber.numberOrZero(window.innerWidth)
            : EzNumber.numberOrZero(element.clientWidth);
    }

    /**
     * @static
     *@public @function
     * Gets an EzDomRect with the DOMRect in  formation for the provided element or id.
     * @param {string} elementRef
     * @returns {EzDomRect}
     */
    static elementClientRect(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        const domRect = element.getBoundingClientRect();

        return EzObject.isValid(domRect)
            ? new EzDomRect(domRect)
            : new EzDomRect(null, 0, 0, 0, 0, 0, 0, 0, 0);
    }

    /**
     *@public @function
     * Sets the element's CSS height value
     * Replaces:
     *  1) globalThis.ezapi.ezclocker.ezUi.ezGetElementHeight(elementOrId)
     * @param {string} elementRef
     */
    static getHeight(elementRef) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            false);

        return EzObject.assignOrDefault(element?.style?.height, 0);
    }

    /**
     *@public @function
     * Sets the element's CSS height value
     * Replaces:
     *  1) globalThis.ezapi.ezclocker.ezUi.ezSetElementHeight(elementOrId, height)
     * @param {string} elementRef
     * @param {number|string} cssHeight
     */
    static setHeight(elementRef, cssHeight) {
        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.setContent.toString(),
            true);

        element.style.height = EzNumber.isNumber(cssHeight)
            ? `${cssHeight}px`
            : cssHeight;
    }

    /**
     *@public @function
     * Sizes the element associated with the provided childElementId to a percentage of the parent element
     * associated with the provided parentElementId IF percentWidthOfParent and/or percentHeightOfParent is provided.
     * The width or height of the child element is maintained if the respective percent parm is not provided or not
     * a number.
     * If the percent param value is greater than 1, it is divided by 100 before being used to calculate the]
     * child size. Otherwise, the less than 1 value is used to calculate the child size.
     * Child size calculation: parentInner(Width/Height) * precent(Width/Height)OfParent
     * Replaces:
     *  1) ezSizeToParent(parentElementId, childElementId, percentWidthOfParent, percentHeightOfParent)
     *  2) globalThis.ezApi.ezclocker.ezUi.ezSizeToParent$(parentJQuerySelector, childJQuerySelector, percentWidthOfParent, percentHeightOfParent)
     * @param {string} parentRef
     * @param {string} parentChildId
     * @param {undefined|null|number} percentWidthOfParent
     * Default: null
     * @param {undefined|null|number} percentHeightOfParent
     * Default: null
     */
    static sizeToParent(parentRef, parentChildId, percentWidthOfParent = null, percentHeightOfParent = null) {
        if (!EzNumber.isNumber(percentWidthOfParent) && !EzNumber.isNumber(percentHeightOfParent)) {
            this.#log('A valid percent width or height of parent value is required in call to EzUI.ezSizeToParent.', 'error');

            return;
        }

        const element = EzUx.findElement(parentRef);

        if (!EzObject.isValid(element)) {
            return;
        }

        for (let childNode of element.childNodes) {
            if (EzObject.isValid(childNode) && (childNode.id === parentChildId || `#${childNode.id}` === parentChildId)) {
                if (EzNumber.isNumber(percentWidthOfParent)) {
                    const pWidth = EzUx.innerWidth(parentRef);

                    percentWidthOfParent = percentWidthOfParent > EzNumber.ONE
                        ? percentWidthOfParent / EzNumber._100
                        : percentWidthOfParent;

                    const cWidth = Math.floor(pWidth * percentWidthOfParent);

                    EzUx.setWidth(cWidth);
                }

                if (EzNumber.isNumber(percentHeightOfParent)) {
                    const pHeight = EzUx.innerHeight(parentRef);

                    percentHeightOfParent = percentHeightOfParent > EzNumber.ONE
                        ? percentHeightOfParent / EzNumber._100
                        : percentHeightOfParent;

                    const cHeight = Math.floor(pHeight * percentHeightOfParent);

                    EzUx.setHeight(cHeight);
                }
            }
        }
    }

    /**
     *@public @function
     * Sizes the element associated with the provided childSelector to a percentage of the parent element
     * associated with the provided parentSelector IF percentWidthOfParent and/or percentHeightOfParent is provided.
     * The width or height of the child element is maintained if the respective percent parm is not provided or not
     * a number.
     * If the percent param value is greater than 1, it is divided by 100 before being used to calculate the]
     * child size. Otherwise, the less than 1 value is used to calculate the child size.
     * Child size calculation: parentInner(Width/Height) * precent(Width/Height)OfParent
     * Replaces:
     *  1) globalThis.ezApi.ezclocker.ezUi.ezSizeToParent$(parentJQuerySelector, childJQuerySelector, percentWidthOfParent, percentHeightOfParent)
     * @param {string} parentJQuerySelector
     * @param {string} childJQuerySelector
     * @param {undefined|null|number} percentWidthOfParent
     * Default: null
     * @param {undefined|null|number} percentHeightOfParent
     * Default: null
     */
    static sizeToParent$(parentJQuerySelector, childJQuerySelector, percentWidthOfParent, percentHeightOfParent) {
        if (!EzUx.elementExists$(parentJQuerySelector)) {
            this.#log('A valid parent element id is required in call to EzUI.ezSizeToParent.', 'error');

            return;
        }
        if (!EzUx.elementExists$(childJQuerySelector)) {
            this.#log('A valid child element id is required in call to EzUI.ezSizeToParent.', 'error');

            return;
        }
        if (!EzNumber.isNumber(percentWidthOfParent) && !EzNumber.isNumber(percentHeightOfParent)) {
            this.#log('A valid percent width or height of parent value is required in call to EzUI.ezSizeToParent.', 'error');

            return;
        }

        if (EzNumber.isNumber(percentWidthOfParent)) {
            const pWidth = $(parentJQuerySelector).innerWidth();

            percentWidthOfParent = percentWidthOfParent > EzNumber.ONE
                ? percentWidthOfParent / EzNumber._100
                : percentWidthOfParent;

            const cWidth = Math.floor(pWidth * percentWidthOfParent);

            $(childJQuerySelector).width(cWidth);
        }

        if (EzNumber.isNumber(percentHeightOfParent)) {
            const pHeight = $(parentJQuerySelector).innerHeight();

            percentHeightOfParent = percentHeightOfParent > EzNumber.ONE
                ? percentHeightOfParent / EzNumber._100
                : percentHeightOfParent;


            const cHeight = Math.floor(pHeight * percentHeightOfParent);

            $(childJQuerySelector).height(cHeight);
        }
    }

    /**
     * @static
     *@public @function
     * Gets the element's offset top value
     * @param {string} elementRef
     * @return {number}
     */
    static offsetTop(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot enable element element ${elementRef}: Element does not exist.`, 'error');

            return 0;
        }

        return element.offsetTop;
    }

    /**
     * @static
     *@public @function
     * Enables the element
     * @param {string} [elementRef = EzString.EMPTY]
     * @param {undefined|null|boolean} [throwIfMissing=false]
     * Default is false. If true, an error is thrown if the element does not exist. If false, an error is logged to the console if the element does not exist.
     * @returns {boolean}
     * Returns true if the element was enabled, false if the element does not exist and throwIfMissing is false, or an error is
     * thrown if the element does not exist and throwIfMissing is true.
     */
    static enable(elementRef = EzString.EMPTY, throwIfMissing = false) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            if (throwIfMissing) {
                const em = `Cannot enable element element ${elementRef}: Element does not exist.`;

                throw new EzBadParamException(
                    { elementRef },
                    this,
                    this.enable,
                    em);
            }

            return false;
        }

        element.disabled = false;

        return true;
    }

    /**
     * @static
     *@public @function
     * Enables all the element references/ids in the elementRefArray param.
     * @param {undefined|null|array} elementRefArray
     * Default: EzArray.EMPTY
     * @param {undefined|null|boolean} [throwIfMissing=false]
     * Default is false. If true, an error is thrown if the element does not exist. If false, an error is logged to the console if the element does not exist.
     * @returns {array}
     * Returns an array of booleans for each element reference passed in elementRefArray.
     * If true if the element was enabled, false if the element does not exist and throwIfMissing is false, or an error is
     * thrown if the element does not exist and throwIfMissing is true.
     * Note that if elementRefArray is empty, and empty array is returned.
     */
    static enableAll(elementRefArray = EzArray.EMPTY, throwIfMissing = false) {
        if (!EzArray.hasLength(elementRefArray)) {
            return EzArray.EMPTY;
        }

        let results = EzArray.EMPTY;

        elementRefArray.forEach(
            (elementRef) => results.push(
                EzUx.enable(elementRef, throwIfMissing)));

        return results;
    }

    /**
     * @static
     *@public @function
     * Disables the element
     * @param {string} elementRef
     * @param {undefined|null|boolean} [throwIfMissing=false]
     */
    static disable(elementRef, throwIfMissing = false) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            const em = `Cannot disable element ${elementRef}: Element does not exist.`;

            if (EzBoolean.booleanOrFalse(throwIfMissing)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    'elementRef',
                    EzUx,
                    EzUx.disable);
            } else {
                this.#log(em, 'warn');
            }

            return false;
        }

        element.disabled = true;

        return true;
    }

    /**
     * @static
     *@public @function
     * Disables all the element references/ids in the elementRefArray param.
     * @param {undefined|null|array} elementRefArray
     * Default: []
     */
    static disableAll(elementRefArray = EzArray.EMPTY) {
        if (!EzArray.hasLength(elementRefArray)) {
            return;
        }

        elementRefArray.forEach(
            (elementRef) => EzUx.disable(elementRef));
    }

    /**
     * @static
     *@public @function
     * Determines if the elementRef is an existing element and is enabled.
     * @param {string} elementRef
     */
    static isEnabled(elementRef) {
        return EzBoolean.isFalse(
            EzUx.isDisabled(elementRef));
    }

    /**
     * @static
     *@public @function
     * Determines if the elementRef is an existing element and is disabled.
     * @param {string} elementRef
     */
    static isDisabled(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot determine if element ${elementRef} is enabled or disabled: Element does not exist.`, 'error');

            return false;
        }

        return EzBoolean.isTrue(element?.disabled);
    }

    /**
     * @static
     *@public @function
     * Inserts the provided html by:
     *  1) Creating a <span> node with the provided wrapperId
     *  2) Assigning the provided html to the <span> node's innerHTML
     *  3) Inserting the <span> node before the element with the provided elementOrId.
     * @param {string} elementRef
     * @param {undefined|null|string} html
     * Default is: empty string
     * @param {undefined|null|string} wrapperId
     * Default is: 'EzInsertedElementWrapperSpan'
     */
    static insertBeforeElement(elementRef, html, wrapperId) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot insert html before element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        const spanNode = EzUx.ezDocument.createElement('div');

        spanNode.style.display = 'contents';

        spanNode.id = EzString.hasLength(wrapperId)
            ? wrapperId
            : 'EzInsertedElementWrapperSpan';

        spanNode.innerHTML = EzString.stringOrEmpty(html);

        element.parentNode.insertBefore(spanNode, element);
    }

    /**
     * @static
     *@public @function
     * Sets the selected property of the element associated with the elementRef to true.
     * @param {string} elementRef
     */
    static select(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot select element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        element.selected = true;
    }

    /**
     * @static
     *@public @function
     * Sets the selected property of the element associated with the elementRef to false.
     * @param {string} elementRef
     */
    static unselect(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot unselect element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        element.selected = false;
    }

    /**
     * @static
     *@public @function
     * Returns if the element associated with the elementRef has it's selected property set to true.
     * @param {string} elementRef
     * @returns {boolean}
     */
    static isSelected(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot select element ${elementRef}: Element does not exist.`, 'error');

            return false;
        }

        return EzBoolean.isTrue(element?.selected);
    }

    /**
     * @static
     *@public @function
     * Gets the element's dataset value for the provided dataSetKey for the element associated with the provided elementRef.
     * Can exclude the 'data-' prefix and/or send the dataSetKey as camel cased instead of with dashes.
     * @param {string} elementRef
     * @param {string} dataSetKey
     * @returns {*}
     */
    static getDatasetValue(elementRef, dataSetKey) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot get element ${elementRef}'s dataset value for data set key "${dataSetKey}": Element does not exist.`, 'warn');

            return undefined;
        }

        dataSetKey = 0 === dataSetKey.indexOf('data-')
            ? dataSetKey.substring(5).replace('-', EzString.EMPTY)
            : dataSetKey.replace('-', EzString.EMPTY);

        if (!EzString.hasLength(dataSetKey)) {
            throw new EzBadParamException(
                'dataSetKey',
                EzUx,
                EzUx.getDatasetValue);
        }

        return element?.dataset?.[dataSetKey];
    }

    /**
     * @static
     *@public @function
     * Gets the element's dataset value as a JSON object for the provided dataSetKey for the element associated with the provided elementRef.
     * Can exclude the 'data-' prefix and/or send the dataSetKey as camel cased instead of with dashes.
     * @param {string} elementRef
     * @param {string} dataSetKey
     * @returns {object}
     */
    static getDatasetValueAsJsonObject(elementRef, dataSetKey) {
        const dataSetValue = EzUx.getDatasetValue(elementRef, dataSetKey);

        if (!EzString.hasLength(dataSetValue)) {
            throw new EzBadStateException(
                `Expected a valid, non-empty JSON string from element.dataset[${dataSetKey}]`,
                EzString.em`
                    However, the value of element.dataset[${dataSetKey}] is undefined, null, or an empty string.
                    [Dataset Value: ${EzObject.isValid(dataSetValue) ? dataSetValue.toString() : 'undefined or null'}]`,
                EzUx,
                EzUx.getDatasetValueAsJsonObject);
        }

        return EzJson.fromJson(dataSetValue);
    }

    /**
     * @static
     *@public @function
     * Gets the element's dataset value as an int number for the provided dataSetKey for the element associated with the provided elementRef.
     * Can exclude the 'data-' prefix and/or send the dataSetKey as camel cased instead of with dashes.
     * @param {string} elementRef
     * @param {string} dataSetKey
     * @returns {number}
     */
    static getDatasetValueAsInt(elementRef, dataSetKey) {
        const dataSetValue = EzUx.getDatasetValue(elementRef, dataSetKey);

        if (!EzNumber.isNumber(dataSetValue) && !EzString.hasLength(dataSetValue)) {
            throw new EzBadStateException(
                `Expected a valid number (or string representing a number) from element.dataset[${dataSetKey}]`,
                EzString.em`
                    However, the value element.dataset[${dataSetKey}] is either undefined, null, not a string representing a number, or not a number.
                    [Dataset Value: ${EzObject.isValid(dataSetValue) ? dataSetValue.toString() : 'undefined or null'}]`,
                EzUx,
                EzUx.getDatasetValueAsInt);
        }

        return EzNumber.strToInt(dataSetValue);
    }

    /**
     *@public @function
     * Sets an input's readonly property to true
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetInputReadOnly(elementOrId)
     * @param {string} elementRef
     */
    static setInputReadOnly(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot set input ${elementRef} to readonly: Element does not exist.`, 'error');

            return null;
        }

        element.readOnly = true;
    }

    /**
     * @static
     *@public @function
     * Determines if the element or element with the provided id is checked or not.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezIsCheckBoxChecked(elementOrId)
     *  globalThis.ezApi.ezclocker.ezUi.ezIsCheckboxChecked(elementOrId)
     * @param {string} elementRef
     * @returns {boolean}
     */
    static isCheckboxChecked(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot determine if checkbox ${elementRef} is checked or not: Element does not exist.`, 'error');

            return false;
        }

        return EzBoolean.booleanOrFalse(element.checked);
    }

    /**
     * @static
     *@public @function
     * Sets an HTML checkbox input's checked value
     * Replaces
     *  globalThis.ezApi.ezclocker.ezUi.ezSetCheckboxValue(elementOrId, checked)
     * @param {string} elementRef
     * @param {boolean} checked
     */
    static setCheckboxValue(elementRef, checked) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot set checkbox ${elementRef} value to ${checked}: Element does not exist.`, 'error');

            return;
        }

        element.checked = EzBoolean.booleanOrFalse(checked);
    }

    /**
     * @static
     * @public @function
     * Sets an HTML radio input's checked value
     * Replaces
     *  EzUx.ezRadioBoxValue(elementRef, checked = false, throwOnError = false)
     *  globalThis.ezApi.ezclocker.ezUi.ezSetRadioBoxValue(elementOrId, checked)
     * @param {string} elementRef
     * @param {boolean} checked
     * Default: false
     * @param {undefined|null|boolean} throwOnError
     */
    static setRadioBoxValue(elementRef, checked = false, throwOnError = false) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            const em = EzString.em`
                Cannot set radio box ${elementRef} value to ${EzBoolean.booleanOrFalse(checked)} due to the following error:
                The provided elementRef parameter is either undefined, null, or is not associated with an HTML element in the
                current document.`;

            if (EzBoolean.booleanOrFalse(throwOnError)) {
                this.#log(em, 'error');

                throw new EzBadParamException(
                    { elementRef },
                    EzUx,
                    EzUx.setRadioBoxValue);
            } else {
                this.#log(em, 'warn');
            }

            return;
        }

        element.checked = EzBoolean.booleanOrFalse(checked);
    }

    /**
     * @static
     *@public @function
     * Sets an HTML radio input's checked value
     * Replaces
     *  globalThis.ezApi.ezclocker.ezUi.ezSetRadioBoxValue(elementOrId, checked)
     * @param {string} elementRef
     * @param {boolean} checked
     * Default: false
     * @deprecated
     * Migrated to: EzUx.setRadioBoxValue(elementRef, checked)
     */
    static ezSetRadioBoxValue = EzUx.setRadioBoxValue;

    /**
     * @static
     * @public @function
     * Returns if the radio box is checked
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezIsRadioBoxChecked(elementOrId)
     * @param {Object|String} elementRef
     * @param {undefined|null|boolean} [defaultValue=false]
     * Optional value to return if the element does not exist or the elementRef is undefined/null.
     * @returns {boolean}
     */
    static isRadioBoxChecked(elementRef, defaultValue = false) {
        const element = EzUx.findElement(elementRef);

        return EzObject.isValid(element)
            ? EzBoolean.booleanOrDefault(
                element?.checked,
                EzBoolean.booleanOrFalse(defaultValue))
            : EzBoolean.booleanOrFalse(defaultValue);
    }

    /**
     * @static
     *@public @function
     * Adds the error highlight to an input editor
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezShowInputError
     * @param {string} elementRef
     */
    static showInputError(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot show input error for element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        // Class ezInputErrorHighlight is located in ez-inputs.css
        if (!element.classList.contains('ezInputErrorHighlight')) {
            element.classList.add('ezInputErrorHighlight');
        }
    }

    /**
     * @static
     *@public @function
     * Sets the value of the select input identified by
     * Replaces
     *  globalThis.ezApi.ezclocker.ezUi.ezSetSelectValue(elementRef, value)
     * @param {string} elementRef
     * @param {*} value
     */
    static setSelectValue(elementRef, value) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot set select input ${elementRef} value to ${value}: Element does not exist.`, 'error');

            return;
        }

        if (EzUx.elementExists(elementRef)) {
            EzUx.setInputValue(elementRef, value);
        } else {
            // Assume it is a JQuery UI selector instead, and use the older jquery
            EzUx.setSelectValue$(elementRef, value);
        }
    }

    /**
     * @static
     * @public @function
     * Sets the value of the select input identified by the provided jquery selector
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezSetSelectValue$(jquerySelector, value)
     * @param {string} jquerySelector
     * @param {*} value
     */
    static setSelectValue$(jquerySelector, value) {
        const jqueryElement = EzUx.$(jquerySelector);

        if (!EzObject.isValid(jqueryElement)) {
            this.#log(`Cannot set jQuery select input ${jquerySelector} value to ${value}: Element does not exist.`, 'error');

            return;
        }

        jqueryElement.val(value);

        jqueryElement.selectmenu('refresh');
    }

    /*
     * @static
     * @public @function
     * Gets the select input associated by the ${elementRef}'s value
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezGetSelectValue(id)
     * @param {string} elementRef
     */
    static getSelectValue(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot get the select input element ${elementRef}'s select value: Element does not exist.`, 'error');

            return null;
        }

        return element?.value;
    }

    /**
     * @static
     *@public @function
     * Removes the element or element with id from the globalThis.document.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.ezRemoveElement(elementOrId)
     * @param {string} elementRef
     */
    static removeElement(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot remove element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        element.remove();
    }

    /**
     * @static
     *@public @function
     * Selects all the text in an input.
     * Replaces:
     *  1) globalThis.ezApi.ezclocker.ezUi.ezSelectAll(id)
     *  2) globalThis.ezApi.ezclocker.ezUi.ezInputSelectAll(id)
     *  3) globalThis.ezApi.ezclocker.ezUi.ezInputSelectAll$(selector)
     * @param {string} elementRef
     */
    static selectAll(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            this.#log(`Cannot select all in input element ${elementRef}: Element does not exist.`, 'error');

            return;
        }

        element.focus();

        element.select();
    }

    /**
     * @static
     *@public @function
     * Sets a css style property on the element or element with the provided id
     * Replaces
     * globalThis.ezApi.ezclocker.ezUi.ezSetElementCss(elementOrId, cssName, cssValue)
     * @param {string} elementRef
     * @param {string} cssPropertyName
     * @param {undefined|null|any} cssPropertyValue
     * Default: null
     */
    static setCssProperty(elementRef, cssPropertyName, cssPropertyValue = null) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            // No element to set the css style property for
            return;
        }

        let nextUpper = false;

        let newCssName = EzString.EMPTY;

        for (const cssPropertyChar of cssPropertyName) {
            if (nextUpper) {
                newCssName = `${newCssName}${cssPropertyChar.toUpperCase()}`;

                nextUpper = false;
            } else if ('-' === cssPropertyChar) {
                nextUpper = true;
            } else {
                newCssName = `${newCssName}${cssPropertyChar}`;
            }
        }

        element.style[newCssName] = cssPropertyValue;
    }

    /**
     * @static
     *@public @function
     * Adds a new class to the element or element with provided id
     * Replacement for the following globalThis.ezApi.ezclocker.ezUi.ezAddClass methods:
     *  1) ezAddElementClass(elementOrId, className)
     *  2) ezAddClass(id, className)
     *  3) ezAddClass$(selector, className)
     * @param {string} elementRef
     * @param {string} className
     * @returns {null|object}
     * Returns the element reference if it exists, null otherwise
     */
    static addCssClass(elementRef, ...classNames) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (!EzArray.hasLength(classNames)) {
            return element;
        }

        for (const className of classNames) {
            if (!element?.classList?.contains(className)) {
                element.classList.add(className);
            }
        }

        return element;
    }

    /**
     * @static
     *@public @function
     * Removes a CSS class from the element associatd with elementRef
     * Replacement for the following globalThis.ezApi.ezclocker.ezUi.ezAddClass methods:
     *  1) ezRemoveElementClass(elementOrId, className)
     * @param {string} elementRef
     * @param {string} className
     * @returns {null|object}
     * Returns the element reference if it exists, null otherwise
     */
    static removeCssClass(elementRef, className) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (!EzString.hasLength(className)) {
            return element;
        }

        if (element?.classList?.contains(className)) {
            element.classList?.remove(className);
        }

        return element;
    }

    /**
         *@public @function
         * Obtains a data attribute from the element or element associated with the provided id.
         * No need to include the 'data-' portion of the attribute. If included, it is removed before accessing the
         * elements dataset.
         * Replaces:
         * 1) globalThis.ezApi.ezUi.ezGetElementDataAttributeValue(elementOrId, dataAttributeName)
         * @param {object|string} elementRef
         * @param {string} dataAttributeName
         * @returns {string}
         */
    static getElementDataAttributeValue(elementRef, dataAttributeName) {
        if (!EzString.hasLength(dataAttributeName)) {
            throw new EzBadParamException(
                'dataAttributeName',
                EzUx.ezInstance,
                EzUx.ezInstance.ezGetElementDataAttributeValue);
        }

        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (0 === dataAttributeName.indexOf('data-')) {
            dataAttributeName = dataAttributeName.substring(EzNumber.FIVE);
        }

        dataAttributeName = dataAttributeName.replace('-', EzString.EMPTY);

        if (!EzObject.hasProperty(element.dataset, dataAttributeName)) {
            return EzString.EMPTY;
        }

        const value = element.dataset[dataAttributeName];

        return EzString.stringWithTextOrEmpty(value);
    }

    /**
     *@public @function
     * Sets a data attribute on the element or element associated with the provided id.
     * No need to include the 'data-' portion of the attribute. If included, it is removed before accessing the
     * elements dataset.
     * Replaces:
     * 1) globalThis.ezApi.ezclocker.ezUi.ezSetElementDataAttributeValue(elementOrId, dataAttributeName, dataAttributeValue)
     * @param {object|string} elementRef
     * @param {string} dataAttributeName
     * @param {string|null} dataAttributeValue
     */
    static setElementDataAttribute(elementRef, dataAttributeName, dataAttributeValue) {
        if (!EzString.hasText(dataAttributeName)) {
            throw new EzBadParamException(
                'dataAttributeName',
                EzUx.ezInstance,
                EzUx.ezInstance.ezSetElementDataAttributeValue);
        }

        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (0 === dataAttributeName.indexOf('data-')) {
            dataAttributeName = dataAttributeName.substring(EzNumber.FIVE);
        }

        const nameParts = dataAttributeName.split('-');

        let realDatAttributeName = EzString.EMPTY;

        // Converting to camal case
        for (let index = EzNumber.FIRST_INDEX;index < nameParts.length;index++) {
            if (0 === index) {
                realDatAttributeName += index;
            } else {
                realDatAttributeName += `${nameParts[index].charAt(EzNumber.FIRST_INDEX).toUpperCase()}${nameParts[index].substring(EzNumber.ONE)}`;
            }
        }

        element.dataset[realDatAttributeName] = EzString.stringWithTextOrEmpty(dataAttributeValue);
    }

    /**
     * @static
     *@public @function
     * Sets an attribute on the element identified by id
     * Replaces:
     * globalThis.ezApi.ezclocker.ezUi.ezSetElementAttribute(id, attributeName, attributeValue)
     * @param {string} id
     * @param {string} attributeName
     * @param {undefined|null|string} attributeValue
     * Default: EzString.EMPTY
     */
    static setElementAttribute(elementRef, attributeName, attributeValue = EzString.EMPTY) {
        if (!EzString.hasLength(attributeName)) {
            this.#log('Cannot set an element attribute with a undefined, null, or blank attributeName', 'error');

            return;
        }

        const element = EzUx.getElementForElementRef(
            elementRef,
            'setElementAttribute',
            true);

        element[attributeName] = EzString.stringOrEmpty(attributeValue);

        // Original
        // EzUI.ezInstance.ezId$(selector).attr(attributeName, attributeValue);
    }

    /**
     * @static
     *@public @function
     * Builds an element id from the provided entity id and prefix
     * Replaces:
     * globalThis.ezApi.ezclocker.ezUi.ezBuildEntityElementId(entityId, idPrefix, idSuffix)
     * @param {string|number} entityId
     * @param {undefined|null|string} idPrefix
     * @param {undefined|null|string} idSuffix
     */
    static buildEntityElementId(entityId, idPrefix = 'EzElement', idSuffix = EzString.EMPTY) {
        if (!EzNumber.isNumber(entityId) && !EzString.hasLength(entityId)) {
            throw new EzBadParamException(
                'entityId',
                EzUx,
                EzUx.buildEntityElementId);
        }

        const DEFAULT_ID_PREFIX = 'EzElement';

        return EzNumber.isNumber(entityId)
            ? EzUx.buildId`
                ${EzString.stringWithTextOrDefault(idPrefix, DEFAULT_ID_PREFIX)}_
                ${EzNumber.asString(entityId)}_
                ${EzString.stringWithTextOrDefault(idSuffix, EzString.EMPTY)}`
            : EzUx.buildId`
                ${EzString.stringWithTextOrDefault(idPrefix, DEFAULT_ID_PREFIX)}_
                ${EzString.stringWithLengthOrDefault(entityId, 'EntityId')}_
                ${EzString.stringWithTextOrDefault(idSuffix, EzString.EMPTY)}`;
    }

    /**
     *@public @function
     * Removes all spaces and line feeds from the provided aTemplateLitteralValue param. Then returns
     * the trimmed value.
     * Result template: '${value_with_no_space_linefeeds}';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    static buildId(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzArray.hasLength(aStrings)) {
            for (let x = 0;x < aStrings.length;x++) {
                let aString = aStrings[x];

                if (EzNumber.isNumber(aString) || EzBoolean.isBoolean(aString)) {
                    aString = aString.toString();
                }

                aString = EzString.hasLength(aString)
                    ? aString.trim()
                    : EzString.EMPTY;

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzNumber.isNumber(aKey) || EzBoolean.isBoolean(aKey)
                    ? aKey.toString()
                    : EzString.stringOrEmpty(aKey);

                const cleanValue = aString.trimStart().replace(/[ \n\r\t]+/g, EzString.EMPTY);

                const cleanLine = `${cleanValue}${aKey}`.trim();

                cleanValues = `${cleanValues}${cleanLine}`.trim();
            }
        }

        return `${cleanValues}`.trim();
    }

    /**
     *@public @function
     * Sets the position of the scroll bar element associated with the provided id
     * Replaces:
     *  globalThis.ezApi.ezUi.ezScrollTo(elementOrId, x, y)
     * @param {object|string}} elementRef
     * @param {number} x
     * @param {number} y
     */
    static scrollTo(elementRef, x, y) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        if (EzNumber.isNumber(x)) {
            element.scrollTop = x;
        }

        if (EzNumber.isNumber(y)) {
            element.scrollLeft = y;
        }
    }

    /**
     *@public @function
     * Sets the position of the scroll bars associated with the provided id to top left
     * Replaces:
     *  globalThis.ezApi.ezUi.ezScrollToTop(elementRef) {
     * @param {object|string} elementRef
     */
    static scrollToTop(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return null;
        }

        element.scrollTop = 0;

        element.scrollLeft = 0;
    }

    /**
     *@public @function
     * Attempts to scroll the provided element or element with provided id into view.
     * Replaces:
     *  1) globalthis.ezApi.ezclocker.ezUi.ezScrollIntoView(elementOrId)
     *  2) globalthis.ezApi.ezclocker.ezUi.ezElementScrollIntoViewIfNeeded(elementOrId)
     * @param {object|string} elementRef
     * HTML element reference or Id of HTML element.
     */
    static scrollIntoViewIfNeeded(elementRef) {
        const element = EzUx.findElement(elementRef);

        if (EzObject.isValid(element)) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
        }
    }

    /**
     *@public @function
     * Sets an HTML element's title property
     * Replaces:
     *  globalThis.ezApi.ezUi.ezSetElementTitle(elementOrId, title)
     * @param {object|string} elementRef
     * @param {string} title
     */
    static setElementTitle(elementRef, title = EzString.EMPTY) {
        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            return;
        }

        element.title = EzString.stringWithTextOrEmpty(title);
    }

    /**
     *@public @function
     * Submits the form represented by the elementOrId param.
     * @param {object|string} elementRef
     */
    static submitForm(elementRef) {
        if (!EzObject.isValid(elementRef)) {
            throw new EzBadParamException(
                'elementRef',
                EzUx,
                EzUx.submitForm);
        }

        const element = EzUx.getElementForElementRef(
            elementRef,
            EzUx.submitForm.toString(),
            true);

        if (!EzObject.isValid(element)) {
            throw new EzBadParamException(
                'elementRef',
                EzUx,
                EzUx.submitForm);
        }

        element.submit();
    }

    /**
     * @static
     *@public @function
     * Calls 'open' on a jquery-ui dialog with the associated dialogElementId.
     * Replaces:
     * 1) globalThis.ezApi.ezclocker.ezUi.ezShowDialog(dialogId)
     * @param {string} dialogElementId
     * @param {undefined|null|boolean} [throwOnError=false]
     */
    static showDialog(dialogElementId, throwOnError = false) {
        if (!EzString.hasText(dialogElementId)) {
            const em = 'Unable to display dialog due to the following error: The provided dialogElementId is undefined, null, or blank.';
            if (EzBoolean.booleanOrFalse(throwOnError)) {
                this.#log(
                    em,
                    'error');

                throw new EzBadParamException(
                    { dialogElementId },
                    EzUx,
                    EzUx.closeDialog,
                    em);
            } else {
                this.#log(
                    em,
                    'error');
            }

            return;
        }

        if (!EzUx.elementExists(dialogElementId)) {
            const em = EzString.em`
                Unable to display dialog with dialogId=${dialogElementId}.
                HTML element does not exist in the current page.`;

            if (EzBoolean.booleanOrFalse(throwOnError)) {
                this.#log(em, 'error');

                throw new EzException(
                    em,
                    EzUx,
                    EzUx.closeDialog);
            } else {
                this.#log(em, 'warn');
            }

            return;
        }

        try {
            EzUx.$id(dialogElementId)?.dialog('open');
        } catch (err) {
            const em = EzString.em`
                Unable to display dialog with dialogId=${dialogElementId}.
                Error reported: ${EzString.stringWithTextOrDefault(err.message, 'No reason given')}`;

            if (EzBoolean.booleanOrFalse(throwOnError)) {
                this.#log(em, 'error');

                throw new EzException(
                    em,
                    EzUx,
                    EzUx.closeDialog,
                    em);
            } else {
                this.#log(em, 'warn');
            }
        }
    }

    /**
     * @static
     * @public @function
     * Calls 'close' on a jquery-ui dialog with the associated dialogElementId.
     * Replaces:
     * 1) globalThis.ezApi.ezclocker.ezUi.ezCloseDialog(dialogId)
     * @param {string} dialogElementId
     * @param {undefined|null|boolean} [throwOnError=false]
     */
    static closeDialog(dialogElementId, throwOnError = false) {
        if (!EzString.hasText(dialogElementId)) {
            if (!EzBoolean.booleanOrFalse(throwOnError)) {
                this.#log('Unable to close a dialog due to the following error: The provided dialogElementId is undefined, null, or blank.', 'warn');

                return;
            }

            throw new EzBadParamException(
                { dialogElementId },
                EzUx,
                EzUx.closeDialog);
        }

        try {
            EzUx.$id(dialogElementId)?.dialog('close');
        } catch (err) {
            const em = EzString.em`
                Unable to close dialog with dialogId=${dialogElementId}.
                Error reported: ${EzString.stringWithTextOrDefault(err.message, 'No reason given')}`;

            if (EzBoolean.booleanOrFalse(throwOnError)) {
                this.#log(em, 'error');

                throw new EzException(
                    em,
                    EzUx,
                    EzUx.closeDialog,
                    em);
            } else {
                this.#log(em, 'warn');
            }
        }
    }

    /**
     * @static
     * @public @function
     * Executes document.querySelectorAll(selector) passing the provided selector and returns the result.
     * Example use:
     *   EzUx.querySelectorAll('[id^="editButton_"], [id^="deleteButton_"]').forEach( ... )
     * @param {string} selector
     * @returns {NodeList}
     */
    static querySelectorAll(selector) {
        if (!EzString.hasText(selector)) {
            throw new EzBadParamException(
                { selector },
                EzUx,
                EzUx.querySelector);
        }

        return EzUx.ezDocument.querySelectorAll(selector);
    }

    /**
     * @static
     * @public @function
     * Executes document.querySelector(selector) passing the provided selector and returns the result.
     * Example use:
     *   EzUx.querySelectorAll('[id^="editButton_"], [id^="deleteButton_"]').forEach( ... )
     * @param {string} selector
     * @returns {NodeList}
     */
    static querySelector(selector) {
        if (!EzString.hasText(selector)) {
            throw new EzBadParamException(
                { selector },
                EzUx,
                EzUx.querySelector);
        }

        return EzUx.ezDocument.querySelector(selector);
    }

    /**
     * @static
     * @public @function
     * Sets an html image element's source property to the provided src value.
     * Replaces:
     *  globalThis.ezApi.ezclocker.ezUi.setImgElementSrc(id, imageSrc)
     * @param {object|string} [elementRef=null]
     * The reference to the HTML image element.
     * @param {string} [src=EzString.EMPTY]
     * The source URL for the image.
     * @param {boolean} [throwIfFailed=false]
     * If true, the method will throw an exception if the elementRef is invalid or if the element associated with the elementRef does not exist.
     */
    static setImgElementSrc(elementRef = null, src = EzString.EMPTY, throwIfFailed = false) {
        if (null == elementRef) {
            if (throwIfFailed) {
                throw new EzBadParamException(
                    { elementRef },
                    EzUx,
                    EzUx.setImgElementSrc);
            }

            return;
        }

        const element = EzUx.findElement(elementRef);

        if (!EzObject.isValid(element)) {
            if (throwIfFailed) {
                throw new EzBadParamException(
                    { elementRef },
                    EzUx,
                    EzUx.setImgElementSrc,
                    `Could not find an img HTML element associated with the provided ${elementRef}.`);
            }

            return;
        }

        element.src = EzString.stringWithTextOrEmpty(src);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @public @constructor
     * DOES NOT CREATE A NEW INSTANCE
     * Throws exception, only class static method and properties.
     */
    constructor() {
        throw new EzException(
            'Do not create a new instance of EzUx. Access via static method and properties only.');
    }

    /**
     * @static
     * @private @method
     * Handles the document's KEY_UP event to show access key underlins.
     */
    static #ezHandleDocumentKeyDownEvent_ShowAccessKeys(event) {
        if (EzBoolean.isTrue(event.altKey)) {
            const elementsWithAccessKeysOff = globalThis.document.getElementsByClassName(EzUx.ezCssClasses.ezTextClasses.underlineHideAltKeyUp);

            for (const element of elementsWithAccessKeysOff) {
                globalThis.ezApi.ezclocker.ezUi.ezRemoveElementClass(
                    element,
                    EzUx.ezCssClasses.ezTextClasses.underlineHideAltKeyUp);

                globalThis.ezApi.ezclocker.ezUi.ezAddElementClass(
                    element,
                    EzUx.ezCssClasses.ezTextClasses.underlineShowAltKeyDown);
            }
        }
    }

    /**
     * @static
     * @private @method
     * Handles the document's KEY_UP event to hide access key underlins.
     */
    static #ezHandleDocumentKeyUpEvent_HideAccessKeys(event) {
        if (EzBoolean.isFalse(event.altKey)) {
            const elementsWithAccessKeysOn = globalThis?.document?.getElementsByClassName(EzUx.ezCssClasses.ezTextClasses.underlineShowAltKeyDown);

            for (const element of elementsWithAccessKeysOn) {
                globalThis.ezApi.ezclocker.ezUi.ezRemoveElementClass(
                    element,
                    EzUx.ezCssClasses.ezTextClasses.underlineShowAltKeyDown);

                globalThis.ezApi.ezclocker.ezUi.ezAddElementClass(
                    element,
                    EzUx.ezCssClasses.ezTextClasses.underlineHideAltKeyUp);
            }
        }
    }

    /**
     * @static
     * @private @field
     * Stores if the access key show/hide events have been hooked for the document
     * @type {boolean}
     */
    static #ezHookedAccessKeyShowHideEvents = false;

    /**
     * @static @proviate @field
     * If this.#ezHookedAccessKeyShowHideEvents is false, then hooks the documents
     * KEY_DOWN and KEY_UP event in order to show/hide access keys.
     */
    static #ezHookAccessKeyShowHideEvents() {
        if (EzBoolean.booleanOrFalse(this.#ezHookedAccessKeyShowHideEvents)) {
            return;
        }

        this.#ezHookedAccessKeyShowHideEvents = true;

        $(globalThis?.document).ready(
            () => {
                if (globalThis?.window) {
                    // Show access keys on ALT key down
                    globalThis.window.addEventListener(
                        'keydown',
                        this.#ezHandleDocumentKeyDownEvent_ShowAccessKeys);
                    globalThis.window.addEventListener(
                        'keyup',
                        this.#ezHandleDocumentKeyUpEvent_HideAccessKeys);
                }
            });
    }

    /**
     * @static
     * Static initialization of EzUx class.
     */
    static {
        $(document).ready(
            () => this.#ezHookAccessKeyShowHideEvents());
    }


    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

globalThis.EzUx = EzUx;

/**
 * Alternative name for EzUx
 * Provided to try and avoid issues with typos in the name
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzUX } from '/ezlibrary/ux/EzUx.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
globalThis.window.EzUx = EzUx;
